import { useEexCapability } from "../components/eex/use-eex-capability";
import { useFinanceFunction } from "../components/eex/use-finance-function";
import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Dispatch, Fragment, type SetStateAction, useEffect, useRef, useState } from "react";
import { api, selectedCompanyId } from "../lib/api";
import { invalidateEexData } from "../lib/eex-invalidate";
import { createTradeSelectionUpdater } from "../lib/eex-trade-selection";
import { suggestPairName } from "../lib/eex-pair-name";
import { bilateralProjectionPresentation } from "../lib/eex-bilateral-projection";
import { TradeAssignment } from "../components/eex/TradeAssignment";
import { CreatePairDialog } from "../components/eex/CreatePairDialog";
import { useAuth } from "../stores/auth";
import { cn, useDebouncedValue } from "../lib/utils";
import { formatTimestamp } from "../lib/format";
import { todayBucharestIso } from "../lib/intervals";
import { Field, StatusBadge, Td, Th, WarningList, errMessage, withRecognitionConfirm, type GateExtras } from "../components/eex/ui";
import { useEexFeature } from "../lib/eex-features";
import { canonicalPeriod, parsePeriod, bilateralSettlementNatureLabel, type BilateralSettlementNature } from "@chro/shared";
import { useMobileUiV1State } from "../hooks/use-media-query";
import { z } from "zod";
import { parseFinanceOrigin, type FinanceOrigin } from "../features/eex/finance-navigation";
import { FinanceReturnLink } from "../components/eex/FinanceReturnLink";

export const Route = createFileRoute("/eex/trades")({
  validateSearch: (search: Record<string, unknown>): FinanceOrigin & { trade?: string; deal?: string; invalidFocus?: boolean } => {
    const origin = parseFinanceOrigin(search);
    const trade = z.string().uuid().safeParse(search.trade);
    const deal = z.string().uuid().safeParse(search.deal);
    const invalidOrigin = (["financeMonth", "financeView", "financeCompany", "financeTab"] as const)
      .some(key => search[key] !== undefined && !origin[key]);
    return {
      ...origin,
      ...(trade.success ? { trade: trade.data } : {}),
      ...(deal.success ? { deal: deal.data } : {}),
      ...((search.trade !== undefined && !trade.success) || (search.deal !== undefined && !deal.success) || (search.trade !== undefined && search.deal !== undefined) || ((search.trade !== undefined || search.deal !== undefined) && invalidOrigin) ? { invalidFocus: true } : {}),
    };
  },
  component: EexTradesPage,
});

interface ParsedTrade {
  tradeId: string;
  tradedAt: string | null;
  venue: string | null;
  product: string;
  period: string;
  price: number;
  quantity: number;
  side: "B" | "S";
  signedQty: number;
  value: number;
  brokerCode: string | null;
  paidGiven: string | null;
  book: string | null;
  pairLabel: string | null;
  raw?: Record<string, string | null> | null;
}

interface PreviewResult {
  platformName: string | null;
  headerFound: boolean;
  parsed: number;
  skipped: number;
  warnings: string[];
  trades: ParsedTrade[];
}

interface SaveResult {
  ok: boolean;
  headerFound: boolean;
  parsed: number;
  inserted: number;
  duplicates: number;
  skipped: number;
  warnings: string[];
}

interface StoredTrade {
  id: string;
  tradeId: string;
  tradedAt: string | null;
  venue: string | null;
  product: string;
  period: string;
  price: string;
  quantity: number;
  side: "B" | "S";
  signedQty: number;
  value: string;
  currency: string;
  brokerCode: string | null;
  paidGiven: string | null;
  book: string | null;
  pairLabel: string | null;
  eexTraderCode: string | null;
  source: string | null;
  origin: "cleared" | "bilateral";
  counterparty: string | null;
  contractRef: string | null;
  settlementIndex: string | null;
  deliveryStart: string | null;
  deliveryEnd: string | null;
  bilateralDealId: string | null;
  raw: Record<string, string | null> | null;
  assignmentStatus: "pending" | "confirmed";
  assignmentConfirmedAt: string | null;
  hideAttribution: boolean;
  isPrivate: boolean;
  enteredBy: string;
  assignedTo: string;
  assignedToUserId: string | null;
  identityMasked: boolean;
  isMine: boolean;
  canEditPrivacy: boolean;
  canEditAllocation: boolean;
  canEditBilateralDeal: boolean;
  version: number;
  deletedAt: string | null;
  canEdit: boolean;
  editNeedsApproval: boolean;
  pendingChange: "edit" | "delete" | "reassign" | null;
}

interface ChangeRequest {
  id: string;
  type: "edit" | "delete" | "reassign";
  proposed: Record<string, unknown> | null;
  reassignToLabel?: string | null;
  requestedAt: string;
  requestedByEmail: string | null;
  requestedByName: string | null;
  tradeId: string;
  product: string;
  period: string;
  side: "B" | "S";
  signedQty: number;
  price: string;
}

interface BlotterResult {
  trades: StoredTrade[];
  total: number;
  pendingCount: number;
  isAdmin: boolean;
  limit: number;
  offset: number;
}

interface BulkEditResult {
  total: number;
  applied: number;
  pending: number;
  forbidden: number;
  notFound: number;
  blocked: number;
  failed?: number;
  failures?: { trades: string; message: string }[];
}

interface CompanyUser {
  id: string;
  label: string;
  role: string;
}

interface EexAccount {
  id: string;
  name: string;
  kind: string;
  active: boolean;
}

function EexTradesPage() {
  const user = useAuth((s) => s.user);
  const role = user?.role;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const currentCompany = selectedCompanyId() ?? user?.companyId ?? null;
  const wrongCompany = !search.invalidFocus && (!!search.trade || !!search.deal) && !!search.financeCompany && search.financeCompany !== currentCompany;
  const hasTradeFocus = !!search.trade || !!search.deal || !!search.invalidFocus;
  const canManage = useEexCapability("trades.manage");
  const canWrite = (role === "company_admin" || role === "trader") && canManage;
  const { isMobileViewport } = useMobileUiV1State();
  const [bilateralEditDealId, setBilateralEditDealId] = useState<string | null>(null);

  // The platform-name setting now lives on /eex/settings, but a missing value
  // breaks Buy/Sell derivation — so keep it visible on the daily path. (Phase 2b
  // folds this into the cross-page status strip.)
  const settingsQ = useQuery({
    queryKey: ["eex-settings"],
    enabled: role !== "finance",
    queryFn: () => api<{ platformName: string | null }>("/eex/settings"),
  });
  const platformUnset = settingsQ.data != null && !settingsQ.data.platformName;

  return (
    <div className="operations-workspace ops-page ops-trades space-y-6 px-3 py-4 sm:p-4">
      <header className="ops-heading space-y-2">
        {!search.invalidFocus && <FinanceReturnLink origin={search} />}
        <h1 className="font-semibold text-lg">EEX Trades</h1>
        <p className="text-muted text-xs max-w-3xl">
          {search.deal ? "Review the original bilateral contract behind your Finance report. Its legs and calculation periods remain inspectable even without a trade projection." : hasTradeFocus ? "Review the original execution behind your Finance report. Expand its details for the captured source, history and available actions." : <>Shared company book. Pasted trades are auto-assigned to whoever entered them and confirmed
          by a company admin. Buy/Sell is derived from your company&rsquo;s side of each trade.</>}
        </p>
      </header>
      {platformUnset && (
        <Link
          to="/eex/settings"
          className="block rounded-md border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs text-warnOrange underline underline-offset-2 hover:border-warnOrange sm:border-amber-500/60 sm:bg-amber-500/5 sm:text-amber-700 sm:no-underline sm:hover:border-amber-500 dark:sm:text-amber-300"
        >
          Platform name not set — Buy/Sell falls back to the Trade ID suffix. Set it in Settings →
        </Link>
      )}
      {canWrite && !isMobileViewport && !hasTradeFocus && <PastePanel />}
      {canWrite && !isMobileViewport && !wrongCompany && !search.invalidFocus && (!hasTradeFocus || bilateralEditDealId) && (
        <BilateralPanel
          requestedEditDealId={bilateralEditDealId}
          onEditRequestHandled={() => setBilateralEditDealId(null)}
        />
      )}
      {search.deal && <section aria-label="Focused bilateral contract" className="rounded-lg border border-border p-4">
        {search.invalidFocus ? <p role="alert" className="text-negRed">This contract link is invalid. Return to Finance and open the contract again.</p> : wrongCompany ? <p role="alert" className="text-negRed">This Finance link belongs to another company. Switch back to that company to inspect its contract.</p> : <BilateralDealsRegister key={`${currentCompany}:${search.deal}`} focusedDealId={search.deal} readOnly allowEdit={canWrite} onEdit={detail => setBilateralEditDealId(detail.deal.id)} editingDealId={bilateralEditDealId} parentBusy={false} onBusyChange={() => {}} />}
      </section>}
      {!search.deal && <Blotter
        key={`${currentCompany}:${search.invalidFocus ? "invalid" : search.trade ?? "all"}`}
        readOnly={isMobileViewport || role === "finance"}
        onEditBilateralDeal={setBilateralEditDealId}
        focusedTrade={search.trade}
        companyId={currentCompany}
        wrongCompany={wrongCompany}
        invalidFocus={!!search.invalidFocus}
        onClearFocus={() => void navigate({ search: { ...search, trade: undefined, invalidFocus: undefined }, replace: true })}
      />}
      <p className="hidden max-w-3xl text-[10px] text-muted sm:block">
        Positions and pairs live on{" "}
        <Link
          to="/eex/positions-tree"
          className="text-accent underline underline-offset-2 sm:no-underline sm:hover:underline"
        >
          Positions
        </Link>; clearing
        statements on{" "}
        <Link to="/eex/statements" className="text-accent underline underline-offset-2 sm:no-underline sm:hover:underline">Statements</Link>
        {!isMobileViewport && (
          <>; platform name, accounts, privacy and archive import on{" "}
            <Link to="/eex/settings" className="text-accent underline underline-offset-2 sm:no-underline sm:hover:underline">Settings</Link>
          </>
        )}.
      </p>
    </div>
  );
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iP(hone|ad|od)/.test(navigator.platform);

function PastePanel() {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // set by onPaste, consumed by the onChange that the same paste triggers
  const justPasted = useRef(false);
  // mirrors the textarea value so late preview responses for older text are dropped
  const latestText = useRef("");

  const previewMutation = useMutation({
    mutationFn: (t: string) => api<PreviewResult>("/eex/trades/preview", { method: "POST", body: JSON.stringify({ text: t }) }),
    onSuccess: (data, submitted) => {
      if (submitted !== latestText.current) return; // user kept typing / pasted again
      setPreview(data);
      setSaveResult(null);
      setError(null);
    },
    onError: (e) => setError(errMessage(e)),
  });

  const saveMutation = useMutation({
    mutationFn: () => api<SaveResult>("/eex/trades", { method: "POST", body: JSON.stringify({ text }) }),
    onSuccess: (data) => {
      setSaveResult(data);
      setPreview(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
      invalidateEexData(queryClient); // tree/leaf, P&L months, worklist
      void queryClient.invalidateQueries({ queryKey: ["eex-contracts"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-pair-labels"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-trader-codes"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-margin"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-delivery"] });
    },
    onError: (e) => setError(errMessage(e)),
  });

  const canSave = preview != null && preview.trades.length > 0 && !saveMutation.isPending;

  return (
    <section className="ops-panel ops-paste space-y-3">
      <h2 className="font-semibold text-sm">Paste trades</h2>
      <textarea
        value={text}
        onPaste={() => {
          justPasted.current = true;
          // a paste that doesn't change the value fires no onChange — don't let
          // the flag leak onto the next ordinary keystroke (macrotask: the
          // paste's own input event dispatches synchronously before timers)
          setTimeout(() => {
            justPasted.current = false;
          }, 0);
        }}
        onChange={(e) => {
          const next = e.target.value;
          latestText.current = next;
          setText(next);
          setPreview(null);
          setSaveResult(null);
          if (justPasted.current) {
            justPasted.current = false;
            if (next.trim()) previewMutation.mutate(next);
          }
        }}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSave) {
            e.preventDefault();
            saveMutation.mutate();
          }
        }}
        rows={6}
        spellCheck={false}
        placeholder="Paste the Detalii EEX block here, including the header row (Trade ID, Product, Price, Quantity…). Pasting previews automatically."
        className="w-full max-w-5xl bg-transparent border border-border/60 rounded-md px-2 py-2 text-xs font-mono"
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!text.trim() || previewMutation.isPending}
          onClick={() => previewMutation.mutate(text)}
          className="border border-border/60 rounded px-3 py-1 text-sm hover:border-accent/60 disabled:opacity-50"
        >
          {previewMutation.isPending ? "Parsing…" : "Preview"}
        </button>
        <button
          type="button"
          disabled={!canSave}
          onClick={() => saveMutation.mutate()}
          className="rounded px-3 py-1 text-sm bg-accent text-white disabled:opacity-40"
        >
          {saveMutation.isPending ? "Saving…" : `Save${preview ? ` ${preview.trades.length}` : ""}`}
        </button>
        {canSave && <span className="text-[10px] text-muted">{IS_MAC ? "⌘" : "Ctrl+"}↵ saves</span>}
      </div>

      {error && <div className="text-negRed text-xs">{error}</div>}
      {saveResult && (
        <div className="border border-border/60 rounded p-2 text-xs font-mono">
          parsed {saveResult.parsed} · inserted {saveResult.inserted} · duplicates{" "}
          {saveResult.duplicates} · skipped {saveResult.skipped}
        </div>
      )}
      {preview && (
        <div className="space-y-2">
          <div className="text-xs text-muted font-mono">
            parsed {preview.parsed} · skipped {preview.skipped}
            {preview.platformName ? ` · as ${preview.platformName}` : " · platform name not set"}
          </div>
          {preview.warnings.length > 0 && <WarningList warnings={preview.warnings} />}
          {preview.trades.length > 0 && <PreviewTable rows={preview.trades} />}
        </div>
      )}
    </section>
  );
}

type BilateralCurrency = "EUR" | "GBP" | "RON";
type BilateralTopology = "fixed_float" | "float_float";
type BilateralPriceType = "fixed" | "floating";
type BilateralDirection = "receive" | "pay";
type BilateralDocumentKind = "confirmation" | "amendment" | "other";

interface BilateralCatalog {
  instruments: { code: string; label: string; defaultFloatingIndexCode?: string | null }[];
  indexes: { code: string; label: string; currency: BilateralCurrency }[];
}

interface BilateralLegDraft {
  priceType: BilateralPriceType;
  currency: BilateralCurrency;
  fixedPrice: string;
  indexCode: string;
  spread: string;
  multiplier: string;
}

interface BilateralAttachment {
  id: string;
  file: File;
  kind: BilateralDocumentKind;
}

interface BilateralDealResponse {
  dealId: string;
  version?: number;
  unchanged?: boolean;
  projectedRows?: number;
  projectionStatus?: string;
  projectionReason?: string | null;
  calculationPeriods?: number;
}

interface BilateralBookingResult {
  dealId: string;
  action: "created" | "updated";
  version?: number;
  unchanged?: boolean;
  projectedRows: number;
  projectionStatus?: string;
  projectionReason?: string | null;
  calculationPeriods: number;
  uploadedDocuments: number;
  failedDocumentNames: string[];
}

interface BilateralDealSummary {
  id: string;
  instrumentCode: string;
  product: string;
  deliveryStart: string;
  deliveryEnd: string;
  calculationFrequency: "monthly" | "whole_term";
  projectionGranularity: "whole_deal" | "per_calculation_period";
  quantity: number;
  topology: BilateralTopology;
  tradeDate: string;
  counterpartyId: string;
  counterpartyName: string;
  assignedToUserId: string | null;
  assignedTraderLabel: string | null;
  assignmentStatus: "pending" | "confirmed";
  book: string | null;
  pairLabel: string | null;
  confirmationRef: string | null;
  notes: string | null;
  settlementNature: BilateralSettlementNature | null;
  settlementMode: "gross" | "net";
  settlementCurrency: BilateralCurrency;
  fxRule: string | null;
  fxFixing: string | null;
  roundingRule: string | null;
  paymentTerms: string | null;
  projectionStatus: "pending" | "projected" | "not_eligible" | "failed";
  version: number;
  canEdit?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BilateralStoredLeg {
  id: string;
  ordinal: number;
  direction: BilateralDirection;
  priceType: BilateralPriceType;
  fixedPrice: string | null;
  currency: BilateralCurrency;
  indexCode: string | null;
  spread: string;
  multiplier: string;
}

interface BilateralStoredPeriod {
  id: string;
  ordinal: number;
  deliveryStart: string;
  deliveryEnd: string;
  label: string;
  projectedTradeId: string | null;
}

interface BilateralStoredDocument {
  id: string;
  documentKind: BilateralDocumentKind;
  fileName: string;
  contentType: string;
  sha256: string;
  sizeBytes: number;
  uploadedBy: string;
  createdAt: string;
}

interface BilateralDealDetailResponse {
  deal: BilateralDealSummary;
  legs: BilateralStoredLeg[];
  periods: BilateralStoredPeriod[];
  documents: BilateralStoredDocument[];
  projectionAssessment: {
    structuralProjectionEligible: boolean;
    valuationMode: "eex_native" | "ron_opcom_reference_fx" | null;
    referenceValuationEligible: boolean;
    projectionReasonCode: string | null;
    projectionReason: string | null;
  };
  pairingEffect: "visual_only";
}

const CURRENCIES: BilateralCurrency[] = ["EUR", "GBP", "RON"];
const MAX_BILATERAL_MONTHLY_PERIODS = 240;
const BILATERAL_FIELD_CLS = "bg-transparent border border-border/60 rounded px-2 py-1 text-xs";

function newLeg(priceType: BilateralPriceType): BilateralLegDraft {
  return { priceType, currency: "EUR", fixedPrice: "", indexCode: "", spread: "", multiplier: "1" };
}

function calculationPeriodCount(start: string, end: string, frequency: "monthly" | "whole_term"): number | null {
  if (!start || !end || start > end) return null;
  if (frequency === "whole_term") return 1;
  const sy = Number(start.slice(0, 4));
  const sm = Number(start.slice(5, 7));
  const ey = Number(end.slice(0, 4));
  const em = Number(end.slice(5, 7));
  if (![sy, sm, ey, em].every(Number.isFinite)) return null;
  return (ey - sy) * 12 + em - sm + 1;
}

function validBilateralLeg(leg: BilateralLegDraft): boolean {
  const multiplier = Number(leg.multiplier);
  if (!Number.isFinite(multiplier) || multiplier <= 0) return false;
  if (leg.priceType === "fixed") {
    return leg.fixedPrice.trim() !== "" && Number.isFinite(Number(leg.fixedPrice));
  }
  return (
    !!leg.indexCode &&
    (leg.spread.trim() === "" || Number.isFinite(Number(leg.spread)))
  );
}

async function uploadBilateralDocument(dealId: string, attachment: BilateralAttachment): Promise<void> {
  const fd = new FormData();
  fd.append("file", attachment.file);
  fd.append("kind", attachment.kind);
  const companyId = selectedCompanyId();
  const res = await fetch(`/api/eex/bilateral-deals/${encodeURIComponent(dealId)}/documents`, {
    method: "POST",
    credentials: "include",
    headers: companyId ? { "x-company-id": companyId } : undefined,
    body: fd,
  });
  const text = await res.text();
  let body: { error?: string } | null = null;
  if (text) {
    try {
      body = JSON.parse(text) as { error?: string };
    } catch {
      body = null;
    }
  }
  if (!res.ok) throw new Error(body?.error ?? res.statusText);
}

async function downloadBilateralDocument(
  dealId: string,
  documentId: string,
  fileName: string
): Promise<void> {
  const companyId = selectedCompanyId();
  const res = await fetch(
    `/api/eex/bilateral-deals/${encodeURIComponent(dealId)}/documents/${encodeURIComponent(documentId)}/download`,
    {
      credentials: "include",
      headers: companyId ? { "x-company-id": companyId } : undefined,
    }
  );
  if (!res.ok) {
    const text = await res.text();
    let message = res.statusText;
    if (text) {
      try {
        message = (JSON.parse(text) as { error?: string }).error ?? message;
      } catch {
        // Keep the HTTP status text when an intermediary returned non-JSON.
      }
    }
    throw new Error(message);
  }
  const url = URL.createObjectURL(await res.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

// Deal-oriented bilateral entry. The deal and its two cashflow legs are the
// source of truth; any EEX-shaped rows are projections created by the server.
function BilateralPanel({
  requestedEditDealId,
  onEditRequestHandled,
}: {
  requestedEditDealId: string | null;
  onEditRequestHandled: () => void;
}) {
  const licensed = useEexFeature("isda_otc");
  const queryClient = useQueryClient();
  const authUser = useAuth((state) => state.user);
  const formRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [editingDealId, setEditingDealId] = useState<string | null>(null);
  const [editVersion, setEditVersion] = useState<number | null>(null);
  const [registerBusy, setRegisterBusy] = useState(false);
  const [editReferenceLabels, setEditReferenceLabels] = useState<{
    counterpartyName: string;
    assignedTraderLabel: string | null;
    book: string | null;
  } | null>(null);
  const [instrumentCode, setInstrumentCode] = useState("");
  const [deliveryStart, setDeliveryStart] = useState("");
  const [deliveryEnd, setDeliveryEnd] = useState("");
  const [calculationFrequency, setCalculationFrequency] = useState<"monthly" | "whole_term">("monthly");
  const [projectionGranularity, setProjectionGranularity] = useState<"whole_deal" | "per_calculation_period">("whole_deal");
  const [quantity, setQuantity] = useState("");
  const [topology, setTopology] = useState<BilateralTopology>("fixed_float");
  const [receiveLeg, setReceiveLeg] = useState<BilateralLegDraft>(() => newLeg("fixed"));
  const [payLeg, setPayLeg] = useState<BilateralLegDraft>(() => newLeg("floating"));
  const [tradeDate, setTradeDate] = useState(() => todayBucharestIso());
  const [counterpartyId, setCounterpartyId] = useState("");
  const [assignedToUserId, setAssignedToUserId] = useState(() =>
    authUser?.role === "trader" ? authUser.userId : ""
  );
  const [book, setBook] = useState("");
  const [pairLabel, setPairLabel] = useState("");
  const [confirmationRef, setConfirmationRef] = useState("");
  const [notes, setNotes] = useState("");
  const [settlementNature, setSettlementNature] = useState<BilateralSettlementNature | "">("");
  const [settlementMode, setSettlementMode] = useState<"gross" | "net">("gross");
  const [settlementCurrency, setSettlementCurrency] = useState<BilateralCurrency>("EUR");
  const [fxRule, setFxRule] = useState<"no_conversion" | "contractual" | "ecb_trade_date" | "ecb_payment_date">("no_conversion");
  const [fxFixing, setFxFixing] = useState("");
  const [roundingRule, setRoundingRule] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [attachments, setAttachments] = useState<BilateralAttachment[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [result, setResult] = useState<BilateralBookingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const catalogQ = useQuery({
    queryKey: ["eex-bilateral-catalog"],
    enabled: licensed && open,
    queryFn: () => api<BilateralCatalog>("/eex/bilateral-catalog"),
  });
  const counterpartiesQ = useQuery({
    queryKey: ["eex-counterparties"],
    enabled: licensed && open,
    queryFn: () => api<{ counterparties: { id: string; name: string; active: boolean }[] }>("/eex/counterparties"),
  });
  const accountsQ = useQuery({
    queryKey: ["eex-accounts"],
    enabled: licensed && open,
    queryFn: () => api<{ accounts: EexAccount[] }>("/eex/accounts"),
  });
  const labelsQ = useQuery({
    queryKey: ["eex-pair-labels"],
    enabled: licensed && open,
    queryFn: () => api<{ labels: string[] }>("/eex/pair-labels"),
  });
  const assignableUsersQ = useQuery({
    queryKey: ["eex-assignable-users"],
    enabled: licensed && open,
    queryFn: () => api<{ users: { id: string; label: string }[] }>("/eex/assignable-users"),
  });
  const requestedEditQ = useQuery({
    queryKey: ["eex-bilateral-deal", requestedEditDealId],
    enabled: licensed && requestedEditDealId != null,
    queryFn: () =>
      api<BilateralDealDetailResponse>(
        `/eex/bilateral-deals/${encodeURIComponent(requestedEditDealId!)}`
      ),
  });

  useEffect(() => {
    if (!requestedEditDealId || !requestedEditQ.data) return;
    startEdit(requestedEditQ.data);
    onEditRequestHandled();
  }, [requestedEditDealId, requestedEditQ.data, onEditRequestHandled]);

  useEffect(() => {
    if (!requestedEditDealId || !requestedEditQ.isError) return;
    setOpen(true);
    setError(`Could not load the bilateral deal: ${errMessage(requestedEditQ.error)}`);
    onEditRequestHandled();
  }, [requestedEditDealId, requestedEditQ.error, requestedEditQ.isError, onEditRequestHandled]);

  useEffect(() => {
    if (!editingDealId && authUser?.role === "trader" && !assignedToUserId) {
      setAssignedToUserId(authUser.userId);
    }
  }, [assignedToUserId, authUser, editingDealId]);

  const legs = [receiveLeg, payLeg];
  const quantityNumber = Number(quantity);
  const periodCount = calculationPeriodCount(deliveryStart, deliveryEnd, calculationFrequency);
  const periodCountIsValid =
    periodCount != null &&
    (calculationFrequency !== "monthly" || periodCount <= MAX_BILATERAL_MONTHLY_PERIODS);
  const fixedFloatIsValid =
    topology === "float_float"
      ? legs.every((leg) => leg.priceType === "floating")
      : receiveLeg.priceType !== payLeg.priceType && legs.some((leg) => leg.priceType === "fixed");
  const netNeedsFx =
    settlementMode === "net" &&
    (receiveLeg.currency !== settlementCurrency || payLeg.currency !== settlementCurrency);
  const settlementIsValid = !netNeedsFx || fxRule !== "no_conversion";
  const hasFailedUploads = (result?.failedDocumentNames.length ?? 0) > 0;

  function legPayload(direction: BilateralDirection, leg: BilateralLegDraft) {
    return {
      direction,
      priceType: leg.priceType,
      currency: leg.currency,
      fixedPrice: leg.priceType === "fixed" ? Number(leg.fixedPrice) : null,
      indexCode: leg.priceType === "floating" ? leg.indexCode : null,
      spread: leg.priceType === "floating" && leg.spread.trim() !== "" ? Number(leg.spread) : null,
      multiplier: Number(leg.multiplier),
    };
  }

  const save = useMutation({
    mutationFn: async () => {
      const dealId = editingDealId;
      const action = dealId ? "updated" as const : "created" as const;
      const saved = await api<BilateralDealResponse>(
        dealId ? `/eex/bilateral-deals/${encodeURIComponent(dealId)}` : "/eex/bilateral-deals",
        {
          method: dealId ? "PUT" : "POST",
          body: JSON.stringify({
            ...(dealId ? { version: editVersion } : {}),
            instrumentCode,
            deliveryStart,
            deliveryEnd,
            calculationFrequency,
            projectionGranularity,
            quantityMw: quantityNumber,
            topology,
            tradeDate,
            counterpartyId,
            assignedToUserId: assignedToUserId || null,
            book: book || null,
            pairLabel: pairLabel.trim() || null,
            confirmationRef: confirmationRef.trim() || null,
            notes: notes.trim() || null,
            settlementNature: settlementNature || null,
            settlement: {
              mode: settlementMode,
              currency: settlementCurrency,
              fxRule: fxRule === "no_conversion" ? null : fxRule,
              fxFixing: fxRule === "no_conversion" ? null : (fxFixing.trim() || null),
              roundingRule: roundingRule || null,
              paymentTerms: paymentTerms.trim() || null,
            },
            legs: [legPayload("receive", receiveLeg), legPayload("pay", payLeg)],
          }),
        }
      );
      const documentFailures: Array<{ attachment: BilateralAttachment; reason: string }> = [];
      let uploadedDocuments = 0;
      for (const attachment of attachments) {
        try {
          await uploadBilateralDocument(saved.dealId, attachment);
          uploadedDocuments += 1;
        } catch (error) {
          documentFailures.push({ attachment, reason: errMessage(error) });
        }
      }
      return { ...saved, action, uploadedDocuments, documentFailures };
    },
    onMutate: () => {
      setResult(null);
      setError(null);
    },
    onSuccess: (data) => {
      setResult({
        dealId: data.dealId,
        action: data.action,
        version: data.version,
        unchanged: data.unchanged,
        projectedRows: data.projectedRows ?? 0,
        projectionStatus: data.projectionStatus,
        projectionReason: data.projectionReason,
        calculationPeriods: data.calculationPeriods ?? 0,
        uploadedDocuments: data.uploadedDocuments,
        failedDocumentNames: data.documentFailures.map(
          ({ attachment, reason }) => `${attachment.file.name}: ${reason}`
        ),
      });
      setAttachments(data.documentFailures.map(({ attachment }) => attachment));
      setFileInputKey((key) => key + 1);
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
      invalidateEexData(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["eex-contracts"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-pair-labels"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deals"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-margin"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-delivery"] });
      if (data.action === "updated") {
        // An amendment can contain historical dates, counterparties and FX
        // settings. Do not leak any of them into the next new-deal draft.
        resetDealDraft();
      } else {
        // Keep the existing batch-entry convenience for consecutive new deals.
        const defaultIndex =
          instruments.find((instrument) => instrument.code === instrumentCode)
            ?.defaultFloatingIndexCode ?? "";
        const defaultCurrency =
          indexes.find((index) => index.code === defaultIndex)?.currency ?? "EUR";
        setDeliveryStart("");
        setDeliveryEnd("");
        setQuantity("");
        setReceiveLeg({ ...newLeg("fixed"), currency: defaultCurrency });
        setPayLeg({
          ...newLeg("floating"),
          currency: defaultCurrency,
          indexCode: defaultIndex,
        });
        setSettlementCurrency(defaultCurrency);
        setTopology("fixed_float");
        setPairLabel("");
        setConfirmationRef("");
        setNotes("");
        setEditingDealId(null);
        setEditVersion(null);
        setEditReferenceLabels(null);
        setAssignedToUserId(authUser?.role === "trader" ? authUser.userId : "");
      }
    },
    onError: (e) => setError(errMessage(e)),
  });

  const retryDocuments = useMutation({
    mutationFn: async () => {
      if (!result) {
        return {
          uploaded: 0,
          failures: [] as Array<{ attachment: BilateralAttachment; reason: string }>,
        };
      }
      const failures: Array<{ attachment: BilateralAttachment; reason: string }> = [];
      let uploaded = 0;
      for (const attachment of attachments) {
        try {
          await uploadBilateralDocument(result.dealId, attachment);
          uploaded += 1;
        } catch (error) {
          failures.push({ attachment, reason: errMessage(error) });
        }
      }
      return { uploaded, failures };
    },
    onSuccess: (data) => {
      const retryDealId = result?.dealId;
      setAttachments(data.failures.map(({ attachment }) => attachment));
      setResult((previous) => previous && ({
        ...previous,
        uploadedDocuments: previous.uploadedDocuments + data.uploaded,
        failedDocumentNames: data.failures.map(
          ({ attachment, reason }) => `${attachment.file.name}: ${reason}`
        ),
      }));
      if (retryDealId) {
        void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal", retryDealId] });
        void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deals"] });
      }
    },
  });

  const activeCps = (counterpartiesQ.data?.counterparties ?? []).filter((c) => c.active);
  const activeAccounts = (accountsQ.data?.accounts ?? []).filter((a) => a.active);
  const instruments = catalogQ.data?.instruments ?? [];
  const indexes = catalogQ.data?.indexes ?? [];
  const pairLabels = labelsQ.data?.labels ?? [];
  const assignableUsers = (assignableUsersQ.data?.users ?? []).filter(
    (candidate) => authUser?.role !== "trader" || candidate.id === authUser.userId
  );

  const counterpartyOptionExists = activeCps.some((candidate) => candidate.id === counterpartyId);
  const assignedTraderOptionExists = assignableUsers.some((candidate) => candidate.id === assignedToUserId);
  const bookOptionExists = activeAccounts.some((account) => account.name === book);
  const retainingHistoricalCounterparty =
    !!editingDealId && !!editReferenceLabels && !!counterpartyId && !counterpartyOptionExists;
  const retainingHistoricalTrader =
    !!editingDealId && !!editReferenceLabels && !!assignedToUserId && !assignedTraderOptionExists;
  const retainingHistoricalBook =
    !!editingDealId && !!editReferenceLabels && !!book && !bookOptionExists;
  const bookIsAllowed =
    !book ||
    activeAccounts.some((account) => account.name.toLowerCase() === book.toLowerCase()) ||
    retainingHistoricalBook;
  const counterpartyIsAllowed =
    !!counterpartyId && (counterpartyOptionExists || retainingHistoricalCounterparty);
  const assignedTraderIsAllowed =
    !assignedToUserId || assignedTraderOptionExists || retainingHistoricalTrader;
  const mainMutationBusy = save.isPending || retryDocuments.isPending;
  const panelBusy = mainMutationBusy || registerBusy;

  const missingRequiredFields: string[] = [];
  if (!instrumentCode) missingRequiredFields.push("instrument");
  if (!deliveryStart) missingRequiredFields.push("delivery start");
  if (!deliveryEnd) missingRequiredFields.push("delivery end");
  if (deliveryStart && deliveryEnd && !periodCountIsValid) {
    missingRequiredFields.push("valid delivery range");
  }
  if (!Number.isInteger(quantityNumber) || quantityNumber <= 0) {
    missingRequiredFields.push("positive whole-number MW");
  }
  if (!fixedFloatIsValid) missingRequiredFields.push("valid leg structure");
  legs.forEach((leg, index) => {
    const label = index === 0 ? "receive leg" : "pay leg";
    if (leg.priceType === "fixed") {
      if (leg.fixedPrice.trim() === "" || !Number.isFinite(Number(leg.fixedPrice))) {
        missingRequiredFields.push(`${label} fixed price`);
      }
    } else {
      if (!leg.indexCode) missingRequiredFields.push(`${label} floating index`);
      if (leg.spread.trim() !== "" && !Number.isFinite(Number(leg.spread))) {
        missingRequiredFields.push(`${label} numeric spread`);
      }
    }
    const multiplier = Number(leg.multiplier);
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      missingRequiredFields.push(`${label} positive multiplier`);
    }
  });
  if (!editingDealId && !settlementNature) missingRequiredFields.push("physical or financial settlement nature");
  if (!tradeDate) missingRequiredFields.push("trade date");
  if (!counterpartyId) {
    missingRequiredFields.push("counterparty");
  } else if (!counterpartyIsAllowed) {
    missingRequiredFields.push("valid counterparty");
  }
  if (!assignedTraderIsAllowed) missingRequiredFields.push("valid trader");
  if (!bookIsAllowed) missingRequiredFields.push("valid book");
  if (!settlementIsValid) missingRequiredFields.push("FX rule for net settlement");
  if (editingDealId && editVersion == null) missingRequiredFields.push("loaded deal version");
  if (hasFailedUploads) missingRequiredFields.push("retry failed document uploads");

  const canSave =
    missingRequiredFields.length === 0 &&
    legs.every(validBilateralLeg) &&
    !panelBusy;
  const legDiffersFrom = (leg: BilateralLegDraft, initial: BilateralLegDraft) =>
    leg.priceType !== initial.priceType ||
    leg.currency !== initial.currency ||
    leg.fixedPrice !== initial.fixedPrice ||
    leg.indexCode !== initial.indexCode ||
    leg.spread !== initial.spread ||
    leg.multiplier !== initial.multiplier;
  const hasDraftToDiscard =
    editingDealId != null ||
    attachments.length > 0 ||
    !!instrumentCode ||
    !!deliveryStart ||
    !!deliveryEnd ||
    calculationFrequency !== "monthly" ||
    projectionGranularity !== "whole_deal" ||
    !!quantity ||
    topology !== "fixed_float" ||
    legDiffersFrom(receiveLeg, newLeg("fixed")) ||
    legDiffersFrom(payLeg, newLeg("floating")) ||
    tradeDate !== todayBucharestIso() ||
    !!counterpartyId ||
    assignedToUserId !== (authUser?.role === "trader" ? authUser.userId : "") ||
    !!book ||
    !!pairLabel ||
    !!confirmationRef ||
    !!notes ||
    !!settlementNature ||
    settlementMode !== "gross" ||
    settlementCurrency !== "EUR" ||
    fxRule !== "no_conversion" ||
    !!fxFixing ||
    !!roundingRule ||
    !!paymentTerms;

  if (!licensed) return null;

  function resetDealDraft() {
    setEditingDealId(null);
    setEditVersion(null);
    setEditReferenceLabels(null);
    setInstrumentCode("");
    setDeliveryStart("");
    setDeliveryEnd("");
    setCalculationFrequency("monthly");
    setProjectionGranularity("whole_deal");
    setQuantity("");
    setTopology("fixed_float");
    setReceiveLeg(newLeg("fixed"));
    setPayLeg(newLeg("floating"));
    setTradeDate(todayBucharestIso());
    setCounterpartyId("");
    setAssignedToUserId(authUser?.role === "trader" ? authUser.userId : "");
    setBook("");
    setPairLabel("");
    setConfirmationRef("");
    setNotes("");
    setSettlementNature("");
    setSettlementMode("gross");
    setSettlementCurrency("EUR");
    setFxRule("no_conversion");
    setFxFixing("");
    setRoundingRule("");
    setPaymentTerms("");
  }

  function resetToNewDeal() {
    resetDealDraft();
    setAttachments([]);
    setFileInputKey((key) => key + 1);
    setResult(null);
    setError(null);
  }

  function startEdit(detail: BilateralDealDetailResponse) {
    if (panelBusy || editingDealId === detail.deal.id) return;
    if (
      hasDraftToDiscard &&
      !window.confirm(
        attachments.length > 0
          ? "Discard the current bilateral draft and its queued document uploads to edit this deal?"
          : "Discard the current bilateral draft and edit this deal instead?"
      )
    ) {
      return;
    }
    const receive = detail.legs.find((leg) => leg.direction === "receive");
    const pay = detail.legs.find((leg) => leg.direction === "pay");
    if (!receive || !pay) {
      setError("This deal does not have one receive and one pay leg; it cannot be edited safely.");
      return;
    }
    const legDraft = (leg: BilateralStoredLeg): BilateralLegDraft => ({
      priceType: leg.priceType,
      currency: leg.currency,
      fixedPrice: leg.fixedPrice == null ? "" : String(Number(leg.fixedPrice)),
      indexCode: leg.indexCode ?? "",
      spread: Number(leg.spread) === 0 ? "" : String(Number(leg.spread)),
      multiplier: String(Number(leg.multiplier)),
    });
    setOpen(true);
    setEditingDealId(detail.deal.id);
    setEditVersion(detail.deal.version);
    setEditReferenceLabels({
      counterpartyName: detail.deal.counterpartyName,
      assignedTraderLabel: detail.deal.assignedTraderLabel,
      book: detail.deal.book,
    });
    setInstrumentCode(detail.deal.instrumentCode);
    setDeliveryStart(detail.deal.deliveryStart);
    setDeliveryEnd(detail.deal.deliveryEnd);
    setCalculationFrequency(detail.deal.calculationFrequency);
    setProjectionGranularity(detail.deal.projectionGranularity);
    setQuantity(String(detail.deal.quantity));
    setTopology(detail.deal.topology);
    setReceiveLeg(legDraft(receive));
    setPayLeg(legDraft(pay));
    setTradeDate(detail.deal.tradeDate);
    setCounterpartyId(detail.deal.counterpartyId);
    setAssignedToUserId(detail.deal.assignedToUserId ?? "");
    setBook(detail.deal.book ?? "");
    setPairLabel(detail.deal.pairLabel ?? "");
    setConfirmationRef(detail.deal.confirmationRef ?? "");
    setNotes(detail.deal.notes ?? "");
    setSettlementNature(detail.deal.settlementNature ?? "");
    setSettlementMode(detail.deal.settlementMode);
    setSettlementCurrency(detail.deal.settlementCurrency);
    setFxRule((detail.deal.fxRule ?? "no_conversion") as typeof fxRule);
    setFxFixing(detail.deal.fxFixing ?? "");
    setRoundingRule(detail.deal.roundingRule ?? "");
    setPaymentTerms(detail.deal.paymentTerms ?? "");
    setAttachments([]);
    setFileInputKey((key) => key + 1);
    setResult(null);
    setError(null);
    window.requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }

  function selectInstrument(nextCode: string) {
    setInstrumentCode(nextCode);
    const defaultIndex = instruments.find((instrument) => instrument.code === nextCode)?.defaultFloatingIndexCode ?? "";
    const defaultCurrency = indexes.find((index) => index.code === defaultIndex)?.currency;
    setReceiveLeg((leg) => ({
      ...leg,
      ...(defaultCurrency ? { currency: defaultCurrency } : {}),
      ...(leg.priceType === "floating" ? { indexCode: defaultIndex } : {}),
    }));
    setPayLeg((leg) => ({
      ...leg,
      ...(defaultCurrency ? { currency: defaultCurrency } : {}),
      ...(leg.priceType === "floating" ? { indexCode: defaultIndex } : {}),
    }));
    if (defaultCurrency) setSettlementCurrency(defaultCurrency);
  }

  function selectTopology(next: BilateralTopology) {
    setTopology(next);
    if (next === "float_float") {
      setReceiveLeg((leg) => ({ ...leg, priceType: "floating", fixedPrice: "" }));
      setPayLeg((leg) => ({ ...leg, priceType: "floating", fixedPrice: "" }));
    } else {
      setReceiveLeg((leg) => ({ ...leg, priceType: "fixed", indexCode: "", spread: "", multiplier: "1" }));
      setPayLeg((leg) => ({ ...leg, priceType: "floating", fixedPrice: "" }));
    }
  }

  function selectLegType(direction: BilateralDirection, next: BilateralPriceType) {
    if (topology !== "fixed_float") return;
    const other: BilateralPriceType = next === "fixed" ? "floating" : "fixed";
    if (direction === "receive") {
      setReceiveLeg((leg) => ({ ...leg, priceType: next, ...(next === "fixed" ? { indexCode: "", spread: "", multiplier: "1" } : { fixedPrice: "" }) }));
      setPayLeg((leg) => ({ ...leg, priceType: other, ...(other === "fixed" ? { indexCode: "", spread: "", multiplier: "1" } : { fixedPrice: "" }) }));
    } else {
      setPayLeg((leg) => ({ ...leg, priceType: next, ...(next === "fixed" ? { indexCode: "", spread: "", multiplier: "1" } : { fixedPrice: "" }) }));
      setReceiveLeg((leg) => ({ ...leg, priceType: other, ...(other === "fixed" ? { indexCode: "", spread: "", multiplier: "1" } : { fixedPrice: "" }) }));
    }
  }

  return (
    <section className="ops-panel ops-bilateral space-y-3">
      <button
        type="button"
        aria-expanded={open}
        disabled={panelBusy}
        onClick={() => setOpen((value) => !value)}
        className="text-sm font-semibold text-left disabled:opacity-50 disabled:cursor-wait"
      >
        <span className="mr-1 text-muted">{open ? "▾" : "▸"}</span>
        {editingDealId ? "Edit bilateral deal" : "New bilateral deal"}
        <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
          ISDA/OTC
        </span>
      </button>
      {open && (
        <div ref={formRef} className="ops-bilateral-form border border-border/60 rounded-md p-3 max-w-6xl scroll-mt-4">
          <fieldset disabled={panelBusy} className="min-w-0 space-y-5">
          <p className="text-xs text-muted max-w-4xl">
            Record the confirmed delivery window and both cashflow legs. Bilateral deals are excluded
            from clearing-bank and ECC margin reconciliation; eligible fixed/floating periods are
            projected into Positions without pretending the deal is an exchange trade.
          </p>
          {editingDealId && (
            <div className="flex items-center gap-3 rounded border border-accent/40 bg-accent/5 px-3 py-2 text-xs">
              <div>
                <div className="font-semibold">Editing existing bilateral deal</div>
                <div className="font-mono text-[10px] text-muted">{editingDealId} · version {editVersion}</div>
              </div>
              <button type="button" disabled={panelBusy} onClick={resetToNewDeal} className="ml-auto underline text-muted hover:text-fg disabled:opacity-50">
                Cancel edit
              </button>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Deal terms</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end">
              <Field label="Instrument">
                <select value={instrumentCode} onChange={(e) => selectInstrument(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="">Select controlled instrument…</option>
                  {instruments.map((instrument) => <option key={instrument.code} value={instrument.code}>{instrument.label}</option>)}
                </select>
              </Field>
              <Field label="Delivery start">
                <input type="date" value={deliveryStart} onChange={(e) => setDeliveryStart(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`} />
              </Field>
              <Field label="Delivery end">
                <input type="date" min={deliveryStart || undefined} value={deliveryEnd} onChange={(e) => setDeliveryEnd(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`} />
              </Field>
              <Field label="Calculation periods">
                <select value={calculationFrequency} onChange={(e) => setCalculationFrequency(e.target.value as "monthly" | "whole_term")} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="monthly">Monthly (including stubs)</option>
                  <option value="whole_term">Whole delivery term</option>
                </select>
              </Field>
              <label className="col-span-full flex items-center gap-2 text-[10px] text-muted">
                <input
                  type="checkbox"
                  checked={projectionGranularity === "per_calculation_period"}
                  onChange={(e) => setProjectionGranularity(e.target.checked ? "per_calculation_period" : "whole_deal")}
                />
                Create one Positions/blotter row per calculation period
              </label>
              <Field label="MW (integer)">
                <input type="number" min={1} step={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`} />
              </Field>
              <Field label="Structure">
                <select value={topology} onChange={(e) => selectTopology(e.target.value as BilateralTopology)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="fixed_float">Fixed / floating</option>
                  <option value="float_float">Floating / floating</option>
                </select>
              </Field>
            </div>
            {catalogQ.isLoading && <div className="text-[10px] text-muted">Loading the controlled instrument and index catalogue…</div>}
            {catalogQ.isError && <div className="text-xs text-negRed">Instrument catalogue unavailable: {errMessage(catalogQ.error)}</div>}
            {counterpartiesQ.isError && <div className="text-xs text-negRed">Counterparties unavailable: {errMessage(counterpartiesQ.error)}</div>}
            {accountsQ.isError && <div className="text-xs text-negRed">Books unavailable: {errMessage(accountsQ.error)}</div>}
            {labelsQ.isError && <div className="text-xs text-negRed">PAIR labels unavailable: {errMessage(labelsQ.error)}</div>}
            {assignableUsersQ.isError && <div className="text-xs text-negRed">Traders unavailable: {errMessage(assignableUsersQ.error)}</div>}
            {deliveryStart && deliveryEnd && (
              <div className={cn("text-[10px] font-mono", !periodCountIsValid ? "text-negRed" : "text-muted")}>
                {periodCount == null
                  ? "Delivery end must be on or after delivery start."
                  : periodCount > MAX_BILATERAL_MONTHLY_PERIODS
                    ? `Monthly schedules are limited to ${MAX_BILATERAL_MONTHLY_PERIODS} calculation periods.`
                    : `${periodCount} calculation period${periodCount === 1 ? "" : "s"} will be created.`}
                {projectionGranularity === "whole_deal"
                  ? " One Positions/blotter row will represent the full delivery term."
                  : " Each calculation period will create its own Positions/blotter row."}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Cashflow legs</h3>
              <p className="text-[10px] text-muted">Settlement index belongs to its floating leg; every leg has its own currency.</p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <BilateralLegEditor direction="receive" leg={receiveLeg} indexes={indexes} lockType={topology === "float_float"} onTypeChange={(type) => selectLegType("receive", type)} onChange={setReceiveLeg} />
              <BilateralLegEditor direction="pay" leg={payLeg} indexes={indexes} lockType={topology === "float_float"} onTypeChange={(type) => selectLegType("pay", type)} onChange={setPayLeg} />
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Settlement settings</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end">
              <Field label="Settlement nature">
                <select aria-label="Bilateral settlement nature" value={settlementNature} onChange={(e) => setSettlementNature(e.target.value as BilateralSettlementNature | "")} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="">{editingDealId ? "Unclassified — review contract" : "Choose physical or financial…"}</option>
                  <option value="physical">Physical delivery</option>
                  <option value="financial">Financial OTC (ISDA)</option>
                </select>
              </Field>
              <div className="col-span-full text-[10px] text-muted">
                Physical profit is shown separately. Financial OTC settles and is recognized in its delivery month, without ECC variation margin. Cashflow netting does not determine this classification.
              </div>
              <Field label="Cashflow handling">
                <select value={settlementMode} onChange={(e) => setSettlementMode(e.target.value as "gross" | "net")} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="gross">Settle legs separately</option>
                  <option value="net">Net cashflows</option>
                </select>
              </Field>
              <Field label="Settlement currency">
                <select value={settlementCurrency} onChange={(e) => setSettlementCurrency(e.target.value as BilateralCurrency)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  {CURRENCIES.map((currency) => <option key={currency}>{currency}</option>)}
                </select>
              </Field>
              <Field label="FX rule">
                <select value={fxRule} onChange={(e) => setFxRule(e.target.value as typeof fxRule)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="no_conversion">No FX conversion</option>
                  <option value="contractual">Contractual FX fixing</option>
                  <option value="ecb_trade_date">ECB rate on trade date</option>
                  <option value="ecb_payment_date">ECB rate on payment date</option>
                </select>
              </Field>
              <Field label="FX fixing detail">
                <input value={fxFixing} disabled={fxRule === "no_conversion"} onChange={(e) => setFxFixing(e.target.value)} placeholder="Timing / source detail" className={`${BILATERAL_FIELD_CLS} w-full disabled:opacity-50`} />
              </Field>
              <Field label="Rounding">
                <select value={roundingRule} onChange={(e) => setRoundingRule(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="">Confirmation / default</option>
                  <option value="half_up_2dp">Half up, 2 decimals</option>
                  <option value="half_even_2dp">Half even, 2 decimals</option>
                  <option value="truncate_2dp">Truncate, 2 decimals</option>
                </select>
              </Field>
              <Field label="Payment terms">
                <input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} placeholder="e.g. 5 BD after invoice" className={`${BILATERAL_FIELD_CLS} w-full`} />
              </Field>
            </div>
            {netNeedsFx && fxRule === "no_conversion" && (
              <div className="text-xs text-negRed">Netting into a different currency requires an FX rule.</div>
            )}
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Trade administration</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end">
              <Field label="Trade date">
                <input type="date" value={tradeDate} onChange={(e) => setTradeDate(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`} />
              </Field>
              <Field label="Counterparty">
                <select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="">Select managed counterparty…</option>
                  {counterpartyId && !counterpartyOptionExists && (
                    <option value={counterpartyId}>
                      {editReferenceLabels?.counterpartyName ?? "Current counterparty"}
                      {counterpartiesQ.isLoading ? " (loading…)" : " (inactive current — may keep)"}
                    </option>
                  )}
                  {activeCps.map((counterparty) => <option key={counterparty.id} value={counterparty.id}>{counterparty.name}</option>)}
                </select>
              </Field>
              <Field label="Trader (if known)">
                <select value={assignedToUserId} onChange={(e) => setAssignedToUserId(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option
                    value=""
                    disabled={authUser?.role === "trader" && !!editingDealId && !!assignedToUserId}
                  >
                    {authUser?.role === "trader" && !editingDealId ? "Select yourself…" : "Unassigned — assign later"}
                  </option>
                  {assignedToUserId && !assignedTraderOptionExists && (
                    <option value={assignedToUserId}>
                      {editReferenceLabels?.assignedTraderLabel ?? "Current trader"}
                      {assignableUsersQ.isLoading ? " (loading…)" : " (inactive current — may keep)"}
                    </option>
                  )}
                  {assignableUsers.map((trader) => <option key={trader.id} value={trader.id}>{trader.label}</option>)}
                </select>
              </Field>
              <Field label="Book">
                <select value={book} onChange={(e) => setBook(e.target.value)} className={`${BILATERAL_FIELD_CLS} w-full`}>
                  <option value="">—</option>
                  {book && !bookOptionExists && (
                    <option value={book}>
                      {editReferenceLabels?.book ?? book}
                      {accountsQ.isLoading
                        ? " (loading…)"
                        : " (inactive current — may keep)"}
                    </option>
                  )}
                  {activeAccounts.map((account) => <option key={account.id} value={account.name}>{account.name}</option>)}
                </select>
              </Field>
              <Field label="Confirmation reference">
                <input value={confirmationRef} onChange={(e) => setConfirmationRef(e.target.value)} placeholder="LFC / contract no." className={`${BILATERAL_FIELD_CLS} w-full font-mono`} />
              </Field>
              <Field label="PAIR label">
                <input list="bilateral-pair-labels" value={pairLabel} onChange={(e) => setPairLabel(e.target.value)} placeholder="Existing or new label" className={`${BILATERAL_FIELD_CLS} w-full`} />
                <datalist id="bilateral-pair-labels">{pairLabels.map((label) => <option key={label} value={label} />)}</datalist>
              </Field>
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label="Notes">
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${BILATERAL_FIELD_CLS} w-full resize-y`} />
                </Field>
              </div>
            </div>
            <div className="max-w-3xl rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-[10px] text-amber-700 dark:text-amber-300">
              PAIR groups this bilateral deal with rows using the same label in the Positions Tree.
              It does not allocate or recognize the bilateral exposure against EEX. Existing
              cleared-trade label semantics remain unchanged.
            </div>
          </div>

          <div className="space-y-2">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Documents</h3>
              <p className="text-[10px] text-muted">Attach confirmations, amendments, or supporting documents. Files upload after the deal is saved.</p>
            </div>
            <label htmlFor="bilateral-documents" className="sr-only">Choose bilateral deal documents</label>
            <input
              id="bilateral-documents"
              key={fileInputKey}
              type="file"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                const tooLarge = files.filter((file) => file.size > 20 * 1024 * 1024);
                if (tooLarge.length > 0) {
                  setError(`Documents must be 20 MB or smaller: ${tooLarge.map((file) => file.name).join(", ")}`);
                }
                const accepted = files.filter((file) => file.size > 0 && file.size <= 20 * 1024 * 1024);
                setAttachments((current) => [
                  ...current,
                  ...accepted.map((file, index) => ({
                    id: `${Date.now()}-${index}-${file.name}`,
                    file,
                    kind:
                      current.length === 0 && index === 0
                        ? editingDealId ? "amendment" as const : "confirmation" as const
                        : "other" as const,
                  })),
                ]);
              }}
              className="block text-xs text-muted"
            />
            {attachments.length > 0 && (
              <div className="space-y-1 max-w-3xl">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center gap-2 border border-border/40 rounded px-2 py-1 text-xs">
                    <select value={attachment.kind} onChange={(e) => setAttachments((current) => current.map((item) => item.id === attachment.id ? { ...item, kind: e.target.value as BilateralDocumentKind } : item))} className={BILATERAL_FIELD_CLS}>
                      <option value="confirmation">Confirmation</option>
                      <option value="amendment">Amendment</option>
                      <option value="other">Other</option>
                    </select>
                    <span className="truncate">{attachment.file.name}</span>
                    <span className="text-muted tabular-nums ml-auto">{(attachment.file.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => setAttachments((current) => current.filter((item) => item.id !== attachment.id))} className="text-negRed hover:underline">Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" disabled={!canSave} onClick={() => save.mutate()} className="rounded px-3 py-1 text-sm bg-accent text-white disabled:opacity-40">
              {save.isPending
                ? editingDealId ? "Saving amendment…" : "Creating deal…"
                : editingDealId ? "Save changes" : "Create bilateral deal"}
            </button>
            {editingDealId && (
              <button type="button" disabled={panelBusy} onClick={resetToNewDeal} className="border border-border/60 rounded px-3 py-1 text-sm disabled:opacity-50">
                Cancel
              </button>
            )}
            {!canSave && !panelBusy && missingRequiredFields.length > 0 && (
              <span className="text-xs text-negRed">
                Complete before saving: {missingRequiredFields.join("; ")}.
              </span>
            )}
          </div>
          {error && <div className="text-negRed text-xs">{error}</div>}
          {result && (
            <div className={cn("border rounded p-2 text-xs font-mono", result.failedDocumentNames.length > 0 ? "border-amber-500/50" : "border-border/60")}>
              deal {result.dealId} {result.unchanged ? "already up to date" : result.action}
              {result.version != null && ` · version ${result.version}`}
              {result.calculationPeriods != null && ` · ${result.calculationPeriods} calculation periods`}
              {result.projectedRows != null && ` · ${result.projectedRows} position rows`}
              {result.projectionStatus && ` · EEX period mapping: ${bilateralProjectionPresentation(result.projectionStatus, result.projectionReason).label}`}
              {result.uploadedDocuments > 0 && ` · ${result.uploadedDocuments} document${result.uploadedDocuments === 1 ? "" : "s"} uploaded`}
              {result.projectionReason && <div className="mt-1 text-muted">{bilateralProjectionPresentation(result.projectionStatus, result.projectionReason).description}</div>}
              {result.failedDocumentNames.length > 0 && (
                <div className="mt-1 text-amber-700 dark:text-amber-300">
                  Deal is saved, but document upload failed: {result.failedDocumentNames.join(", ")}. Do not create the deal again.
                  <button type="button" disabled={retryDocuments.isPending} onClick={() => retryDocuments.mutate()} className="ml-2 underline disabled:opacity-50">
                    {retryDocuments.isPending ? "Retrying…" : "Retry uploads"}
                  </button>
                </div>
              )}
            </div>
          )}
          <BilateralDealsRegister
            onEdit={startEdit}
            editingDealId={editingDealId}
            parentBusy={mainMutationBusy}
            onBusyChange={setRegisterBusy}
          />
          </fieldset>
        </div>
      )}
    </section>
  );
}

function BilateralDealsRegister({
  focusedDealId = null,
  readOnly = false,
  allowEdit = false,
  onEdit,
  editingDealId,
  parentBusy,
  onBusyChange,
}: {
  focusedDealId?: string | null;
  readOnly?: boolean;
  allowEdit?: boolean;
  onEdit: (detail: BilateralDealDetailResponse) => void;
  editingDealId: string | null;
  parentBusy: boolean;
  onBusyChange: (busy: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const authUser = useAuth((state) => state.user);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(focusedDealId);
  const [documentKind, setDocumentKind] = useState<BilateralDocumentKind>("amendment");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [restoreSummary, setRestoreSummary] = useState<string | null>(null);
  const [deletedDealId, setDeletedDealId] = useState<string | null>(null);
  const [natureDraft, setNatureDraft] = useState<BilateralSettlementNature | "">("");

  const dealsQ = useQuery({
    queryKey: ["eex-bilateral-deals"],
    enabled: !focusedDealId,
    queryFn: () => api<{ deals: BilateralDealSummary[] }>("/eex/bilateral-deals"),
  });
  const deals = dealsQ.data?.deals ?? [];

  useEffect(() => {
    if (focusedDealId) { setSelectedDealId(focusedDealId); return; }
    if (!selectedDealId && deals[0]) setSelectedDealId(deals[0].id);
    if (selectedDealId && !deals.some((deal) => deal.id === selectedDealId)) {
      setSelectedDealId(deals[0]?.id ?? null);
    }
  }, [deals, selectedDealId, focusedDealId]);

  useEffect(() => {
    setDocumentFile(null);
    setFileInputKey((key) => key + 1);
    setRegisterError(null);
  }, [selectedDealId]);

  const detailQ = useQuery({
    queryKey: ["eex-bilateral-deal", selectedDealId],
    enabled: !!selectedDealId && selectedDealId !== deletedDealId,
    queryFn: () => api<BilateralDealDetailResponse>(`/eex/bilateral-deals/${encodeURIComponent(selectedDealId!)}`),
  });

  useEffect(() => {
    setNatureDraft(detailQ.data?.deal.settlementNature ?? "");
  }, [detailQ.data?.deal.id, detailQ.data?.deal.version, detailQ.data?.deal.settlementNature]);

  const classifyNature = useMutation({
    mutationFn: (variables: { dealId: string; version: number; settlementNature: BilateralSettlementNature | null }) =>
      api(`/eex/bilateral-deals/${encodeURIComponent(variables.dealId)}/settlement-nature`, {
        method: "PATCH", body: JSON.stringify({ version: variables.version, settlementNature: variables.settlementNature }),
      }),
    onMutate: () => setRegisterError(null),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal", variables.dealId] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deals"] });
      invalidateEexData(queryClient);
    },
    onError: (error) => setRegisterError(errMessage(error)),
  });

  const upload = useMutation({
    mutationFn: async (variables: { dealId: string; file: File; kind: BilateralDocumentKind }) => {
      if (variables.file.size <= 0) throw new Error("The document is empty.");
      if (variables.file.size > 20 * 1024 * 1024) throw new Error("Documents must be 20 MB or smaller.");
      await uploadBilateralDocument(variables.dealId, {
        id: `later-${Date.now()}-${variables.file.name}`,
        file: variables.file,
        kind: variables.kind,
      });
    },
    onMutate: () => setRegisterError(null),
    onSuccess: (_data, variables) => {
      setDocumentFile(null);
      setFileInputKey((key) => key + 1);
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal", variables.dealId] });
    },
    onError: (error) => setRegisterError(errMessage(error)),
  });

  const restoreWholeDealProjections = useMutation({
    mutationFn: () =>
      api<{
        repaired: Array<{
          dealId: string;
          retiredRows: number;
          tradeId: string;
          period: string;
          projectionStatus: "projected" | "not_eligible";
        }>;
        skipped: Array<{ dealId: string; reason: string }>;
      }>(
        "/eex/bilateral-deals/restore-whole-deal-projections",
        { method: "POST" }
      ),
    onMutate: () => {
      setRegisterError(null);
      setRestoreSummary(null);
    },
    onSuccess: (data) => {
      setRestoreSummary(
        data.repaired.length > 0
          ? `Restored ${data.repaired.length} whole-term bilateral row${data.repaired.length === 1 ? "" : "s"}; ${data.repaired.reduce((sum, item) => sum + item.retiredRows, 0)} superseded monthly rows are retained in history.`
          : "No automatically split bilateral projections needed restoration."
      );
      if (data.skipped.length > 0) {
        setRegisterError(`${data.skipped.length} deal${data.skipped.length === 1 ? " was" : "s were"} left unchanged because their stored links need review.`);
      }
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deals"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
      invalidateEexData(queryClient);
    },
    onError: (error) => setRegisterError(errMessage(error)),
  });

  const confirmAssignment = useMutation({
    mutationFn: (variables: { dealId: string; version: number }) =>
      api<{ ok: true; version: number }>(
        `/eex/bilateral-deals/${encodeURIComponent(variables.dealId)}/confirm-assignment`,
        {
          method: "POST",
          body: JSON.stringify({ version: variables.version }),
        }
      ),
    onMutate: () => setRegisterError(null),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal", variables.dealId] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deals"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
    },
    onError: (error) => setRegisterError(errMessage(error)),
  });

  const deleteDeal = useMutation({
    mutationFn: (deal: BilateralDealSummary) =>
      api(`/eex/bilateral-deals/${encodeURIComponent(deal.id)}`, {
        method: "DELETE", body: JSON.stringify({ version: deal.version }),
      }),
    onMutate: () => setRegisterError(null),
    onSuccess: (_data, deal) => {
      setDeletedDealId(deal.id);
      setRestoreSummary("Bilateral deal deleted. History and documents have been retained.");
      queryClient.setQueryData<{ deals: BilateralDealSummary[] }>(["eex-bilateral-deals"], current =>
        current ? { deals: current.deals.filter(row => row.id !== deal.id) } : current);
      queryClient.removeQueries({ queryKey: ["eex-bilateral-deal", deal.id], exact: true });
      invalidateEexData(queryClient);
    },
    onError: (error) => setRegisterError(errMessage(error)),
  });

  useEffect(() => {
    onBusyChange(upload.isPending || confirmAssignment.isPending || restoreWholeDealProjections.isPending || classifyNature.isPending || deleteDeal.isPending);
  }, [classifyNature.isPending, confirmAssignment.isPending, deleteDeal.isPending, onBusyChange, restoreWholeDealProjections.isPending, upload.isPending]);

  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  const canDownloadDocuments = useFinanceFunction("trades.documents");
  const download = useMutation({
    mutationFn: (document: BilateralStoredDocument) =>
      downloadBilateralDocument(selectedDealId!, document.id, document.fileName),
    onMutate: () => setRegisterError(null),
    onError: (error) => setRegisterError(errMessage(error)),
  });

  const detail = selectedDealId === deletedDealId ? undefined : detailQ.data;
  const projectionDisplay = detail && bilateralProjectionPresentation(
    detail.deal.projectionStatus,
    detail.projectionAssessment.projectionReason,
    detail.projectionAssessment.projectionReasonCode,
  );
  const interactionBusy = parentBusy || upload.isPending || confirmAssignment.isPending || classifyNature.isPending || deleteDeal.isPending || restoreWholeDealProjections.isPending;

  const canConfirmAssignment = !readOnly &&
    (authUser?.role === "company_admin" || authUser?.role === "global_admin") &&
    detail?.deal.assignedToUserId != null &&
    detail.deal.assignmentStatus === "pending" &&
    editingDealId !== detail.deal.id;

  return (
    <div className="border-t border-border/60 pt-4 space-y-3">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{focusedDealId ? "Bilateral contract" : "Recent bilateral deals"}</h3>
        <p className="text-[10px] text-muted">
          Contractual records remain here for every delivery window. EEX period mapping describes standard contract periods; supported custom windows can contribute monthly exposure in Positions.
        </p>
        {!focusedDealId && !readOnly && (authUser?.role === "company_admin" || authUser?.role === "global_admin") && (
          <button
            type="button"
            disabled={interactionBusy || restoreWholeDealProjections.isPending}
            onClick={() => restoreWholeDealProjections.mutate()}
            className="mt-2 rounded border border-accent/50 px-2 py-1 text-[10px] text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            {restoreWholeDealProjections.isPending ? "Restoring whole-deal projections…" : "Restore automatically split bilateral projections"}
          </button>
        )}
        {restoreSummary && <div className="mt-2 text-[10px] text-posGreen">{restoreSummary}</div>}
      </div>

      {!focusedDealId && dealsQ.isLoading && <div className="text-xs text-muted">Loading bilateral deals…</div>}
      {!focusedDealId && dealsQ.isError && <div className="text-xs text-negRed">Deals unavailable: {errMessage(dealsQ.error)}</div>}
      {!focusedDealId && !dealsQ.isLoading && deals.length === 0 && (
        <div className="text-xs text-muted">No bilateral deals have been booked yet.</div>
      )}

      {(focusedDealId || deals.length > 0) && (
        <div className={cn("grid gap-3", !focusedDealId && "lg:grid-cols-[minmax(280px,0.8fr)_minmax(0,2fr)]")}>
          {!focusedDealId && <div className="border border-border/50 rounded-md max-h-80 overflow-auto divide-y divide-border/40">
            {deals.slice(0, 50).map((deal) => (
              <button
                key={deal.id}
                type="button"
                disabled={interactionBusy}
                onClick={() => setSelectedDealId(deal.id)}
                className={cn(
                  "w-full text-left px-2 py-2 text-xs hover:bg-border/10 disabled:opacity-60 disabled:cursor-wait",
                  selectedDealId === deal.id && "bg-accent/10",
                  editingDealId === deal.id && "ring-1 ring-inset ring-accent/50"
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{deal.product}</span>
                  <span className="ml-auto tabular-nums whitespace-nowrap">{deal.quantity} MW</span>
                </div>
                <div className="text-muted font-mono truncate">
                  {deal.deliveryStart} → {deal.deliveryEnd} · {deal.topology === "fixed_float" ? "Fixed/Float" : "Float/Float"} · {bilateralSettlementNatureLabel(deal.settlementNature)}
                </div>
                <div className="flex gap-2 text-[10px] text-muted mt-0.5">
                  <span>{deal.counterpartyName}</span>
                  <span className="truncate">{deal.assignedTraderLabel ?? "Unassigned"}</span>
                  {deal.pairLabel && <span className="truncate">PAIR {deal.pairLabel}</span>}
                  <span className="ml-auto" title="Standard EEX period mapping">{deal.id === detail?.deal.id ? projectionDisplay?.label : bilateralProjectionPresentation(deal.projectionStatus).label}</span>
                </div>
              </button>
            ))}
          </div>}

          <div className="border border-border/50 rounded-md p-3 min-h-40">
            {detailQ.isLoading && <div className="text-xs text-muted">Loading deal detail…</div>}
            {detailQ.isError && <div className="text-xs text-negRed">Deal unavailable: {errMessage(detailQ.error)}</div>}
            {detail && (
              <div className="space-y-4 text-xs">
                <div className="flex items-start gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-sm">{detail.deal.product}</div>
                    <div className="font-mono text-[10px] text-muted">{detail.deal.id}</div>
                  </div>
                  <span className="px-1.5 py-0.5 rounded bg-border/30 text-[10px]">
                    EEX period mapping: {projectionDisplay?.label}
                  </span>
                  {detail.deal.assignmentStatus === "pending" && detail.deal.assignedToUserId && (
                    <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase text-amber-700 dark:text-amber-300">
                      trader pending confirmation
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-3">
                    <span className="text-muted">Trade date {detail.deal.tradeDate}</span>
                    {canConfirmAssignment && (
                      <button
                        type="button"
                        disabled={interactionBusy}
                        onClick={() => confirmAssignment.mutate({
                          dealId: detail.deal.id,
                          version: detail.deal.version,
                        })}
                        className="rounded border border-posGreen/50 px-2 py-1 text-posGreen hover:bg-posGreen/10 disabled:opacity-50"
                      >
                        {confirmAssignment.isPending ? "Confirming…" : "Confirm trader"}
                      </button>
                    )}
                    {(!readOnly || allowEdit) && detail.deal.canEdit && (
                      <button
                        type="button"
                        disabled={interactionBusy || editingDealId === detail.deal.id}
                        onClick={() => {
                          const deal = detail.deal;
                          if (window.confirm(`Delete this bilateral deal?\n\n${deal.product} · ${deal.quantity} MW\n${deal.counterpartyName}\n${deal.deliveryStart} → ${deal.deliveryEnd}${deal.pairLabel ? `\nPAIR ${deal.pairLabel}` : ""}\n\nThis removes the deal and its generated trades from active reports. History and documents will be retained.`)) {
                            deleteDeal.mutate(deal);
                          }
                        }}
                        className="rounded border border-negRed/50 px-2 py-1 text-negRed hover:bg-negRed/10 disabled:opacity-50"
                      >
                        {deleteDeal.isPending ? "Deleting…" : "Delete deal"}
                      </button>
                    )}
                    {(!readOnly || allowEdit) && detail.deal.canEdit && (
                      <button
                        type="button"
                        disabled={interactionBusy || editingDealId === detail.deal.id}
                        onClick={() => onEdit(detail)}
                        className="rounded border border-accent/50 px-2 py-1 text-accent hover:bg-accent/10 disabled:opacity-50 disabled:cursor-default"
                      >
                        {editingDealId === detail.deal.id ? "Editing" : "Edit deal"}
                      </button>
                    )}
                  </div>
                </div>

                <div className={cn(
                  "rounded border px-2 py-1.5 text-[10px]",
                  detail.projectionAssessment.structuralProjectionEligible
                    ? "border-posGreen/30 bg-posGreen/5 text-posGreen"
                    : projectionDisplay?.customWindow
                      ? "border-border/60 bg-border/5 text-muted"
                      : "border-negRed/30 bg-negRed/5 text-negRed"
                )}>
                  {detail.projectionAssessment.structuralProjectionEligible ? (
                    detail.projectionAssessment.valuationMode === "ron_opcom_reference_fx" ? (
                      <>
                        {detail.projectionAssessment.referenceValuationEligible
                          ? "Positions projection eligible · RON/OPCOM reference valuation through dated EUR/RON. It remains an estimate and stays outside Recognition, ECC and VM."
                          : "Positions projection created, but RON/OPCOM reference valuation requires complete calendar-month or ISO-week periods. This full-term row remains unvalued and stays outside Recognition, ECC and VM."}
                      </>
                    ) : (
                      <>Positions projection eligible · native EEX reference valuation.</>
                    )
                  ) : (
                    <>
                      {projectionDisplay?.customWindow
                        ? projectionDisplay.description
                        : `Standard EEX period mapping unavailable: ${projectionDisplay?.description ?? "stored terms require review"}.`}
                    </>
                  )}
                </div>

                <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2 xl:grid-cols-4">
                  <div><span className="text-muted">Delivery:</span> {detail.deal.deliveryStart} → {detail.deal.deliveryEnd}</div>
                  <div><span className="text-muted">Quantity:</span> {detail.deal.quantity} MW</div>
                  <div><span className="text-muted">Counterparty:</span> {detail.deal.counterpartyName}</div>
                  <div><span className="text-muted">Trader:</span> {detail.deal.assignedTraderLabel ?? "Unassigned"}</div>
                  <div><span className="text-muted">Book:</span> {detail.deal.book ?? "—"}</div>
                  <div><span className="text-muted">PAIR:</span> {detail.deal.pairLabel ?? "—"}</div>
                  <div><span className="text-muted">Confirmation:</span> {detail.deal.confirmationRef ?? "—"}</div>
                  <div><span className="text-muted">Settlement nature:</span> {bilateralSettlementNatureLabel(detail.deal.settlementNature)}</div>
                  <div><span className="text-muted">Settlement:</span> {detail.deal.settlementMode === "gross" ? `separate cashflows · ${detail.deal.settlementCurrency} reporting` : `net in ${detail.deal.settlementCurrency}`}</div>
                  <div><span className="text-muted">FX:</span> {detail.deal.fxRule ?? "none"}{detail.deal.fxFixing ? ` · ${detail.deal.fxFixing}` : ""}</div>
                </div>

                {!readOnly && detail.deal.canEdit && (
                  <div className="rounded border border-border/60 bg-border/5 p-3 flex flex-wrap items-end gap-3">
                    <Field label="Economic classification">
                      <select aria-label="Saved bilateral settlement nature" value={natureDraft} disabled={interactionBusy || editingDealId === detail.deal.id}
                        onChange={(event) => setNatureDraft(event.target.value as BilateralSettlementNature | "")} className={BILATERAL_FIELD_CLS}>
                        <option value="">Unclassified — review contract</option>
                        <option value="physical">Physical delivery</option>
                        <option value="financial">Financial OTC (ISDA)</option>
                      </select>
                    </Field>
                    <button type="button" disabled={interactionBusy || editingDealId === detail.deal.id || (natureDraft || null) === (detail.deal.settlementNature ?? null)}
                      onClick={() => classifyNature.mutate({ dealId: detail.deal.id, version: detail.deal.version, settlementNature: natureDraft || null })}
                      className="rounded border border-accent/50 px-3 py-1.5 text-accent disabled:opacity-50">
                      {classifyNature.isPending ? "Saving…" : "Save classification"}
                    </button>
                    <p className="basis-full text-[10px] text-muted">Recorded in contract history. Financial OTC uses delivery-month recognition; physical profit is shown separately. Existing prices, quantities and trade rows are preserved.</p>
                  </div>
                )}

                <div className="grid gap-2 md:grid-cols-2">
                  {detail.legs.map((leg) => {
                    const spread = Number(leg.spread);
                    const multiplier = Number(leg.multiplier);
                    return (
                      <div key={leg.id} className={cn("rounded border px-2 py-2", leg.direction === "receive" ? "border-posGreen/30" : "border-negRed/30")}>
                        <div className={cn("font-semibold capitalize", leg.direction === "receive" ? "text-posGreen" : "text-negRed")}>{leg.direction} leg</div>
                        <div className="font-mono mt-1">
                          {leg.priceType === "fixed"
                            ? `${Number(leg.fixedPrice).toFixed(2)} ${leg.currency}/MWh fixed`
                            : `${leg.indexCode}${spread === 0 ? "" : ` ${spread > 0 ? "+" : ""}${spread}`} · ${leg.currency}${multiplier === 1 ? "" : ` · ×${multiplier}`}`}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div>
                  <div className="font-semibold mb-1">Calculation periods</div>
                  <div className="flex gap-1 flex-wrap">
                    {detail.periods.map((period) => (
                      <span key={period.id} title={`${period.deliveryStart} → ${period.deliveryEnd}`} className="rounded border border-border/50 px-1.5 py-0.5 font-mono text-[10px]">
                        {period.label}{period.projectedTradeId ? " · projected" : ""}
                      </span>
                    ))}
                  </div>
                </div>

                {(detail.deal.paymentTerms || detail.deal.roundingRule || detail.deal.notes) && (
                  <div className="grid gap-1 sm:grid-cols-2">
                    {detail.deal.paymentTerms && <div><span className="text-muted">Payment:</span> {detail.deal.paymentTerms}</div>}
                    {detail.deal.roundingRule && <div><span className="text-muted">Rounding:</span> {detail.deal.roundingRule}</div>}
                    {detail.deal.notes && <div className="sm:col-span-2"><span className="text-muted">Notes:</span> {detail.deal.notes}</div>}
                  </div>
                )}

                <div className="space-y-2">
                  <div className="font-semibold">Documents</div>
                  {detail.documents.length === 0 ? (
                    <div className="text-muted">No documents attached.</div>
                  ) : (
                    <div className="space-y-1">
                      {detail.documents.map((documentRow) => (
                        <div key={documentRow.id} className="flex items-center gap-2 rounded border border-border/40 px-2 py-1">
                          <span className="uppercase text-[10px] text-muted">{documentRow.documentKind}</span>
                          <span className="truncate">{documentRow.fileName}</span>
                          <span className="text-muted ml-auto whitespace-nowrap">{(documentRow.sizeBytes / 1024).toFixed(0)} KB</span>
                          <button
                            type="button"
                            disabled={!canDownloadDocuments || download.isPending}
                            onClick={() => download.mutate(documentRow)}
                            className="text-accent hover:underline disabled:opacity-50"
                          >
                            Download
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!readOnly && <div className="flex items-center gap-2 flex-wrap rounded bg-border/10 px-2 py-2">
                    <select disabled={interactionBusy} value={documentKind} onChange={(event) => setDocumentKind(event.target.value as BilateralDocumentKind)} className={BILATERAL_FIELD_CLS}>
                      <option value="confirmation">Confirmation</option>
                      <option value="amendment">Amendment</option>
                      <option value="other">Other</option>
                    </select>
                    <label htmlFor="bilateral-register-document" className="sr-only">Choose a document for this bilateral deal</label>
                    <input
                      id="bilateral-register-document"
                      key={fileInputKey}
                      type="file"
                      disabled={interactionBusy}
                      onChange={(event) => setDocumentFile(event.target.files?.[0] ?? null)}
                      className="min-w-0 max-w-sm text-[10px] text-muted"
                    />
                    <button
                      type="button"
                      disabled={!selectedDealId || !documentFile || interactionBusy}
                      onClick={() => {
                        if (!selectedDealId || !documentFile) return;
                        upload.mutate({ dealId: selectedDealId, file: documentFile, kind: documentKind });
                      }}
                      className="rounded bg-accent text-white px-2 py-1 disabled:opacity-40"
                    >
                      {upload.isPending ? "Uploading…" : "Attach document"}
                    </button>
                  </div>}
                </div>

                <div className="rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-[10px] text-amber-700 dark:text-amber-300">
                  PAIR does not allocate or recognize this bilateral exposure against EEX; existing
                  cleared-trade label semantics remain unchanged.
                </div>
              </div>
            )}
            {registerError && <div className="text-negRed text-xs mt-2">{registerError}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function BilateralLegEditor({
  direction,
  leg,
  indexes,
  lockType,
  onTypeChange,
  onChange,
}: {
  direction: BilateralDirection;
  leg: BilateralLegDraft;
  indexes: BilateralCatalog["indexes"];
  lockType: boolean;
  onTypeChange: (type: BilateralPriceType) => void;
  onChange: Dispatch<SetStateAction<BilateralLegDraft>>;
}) {
  return (
    <div className={cn("rounded-md border p-3 space-y-3", direction === "receive" ? "border-posGreen/40 bg-posGreen/5" : "border-negRed/40 bg-negRed/5")}>
      <div className={cn("text-sm font-semibold", direction === "receive" ? "text-posGreen" : "text-negRed")}>
        {direction === "receive" ? "Receive leg" : "Pay leg"}
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <Field label="Price type">
          <select value={leg.priceType} disabled={lockType} onChange={(e) => onTypeChange(e.target.value as BilateralPriceType)} className={`${BILATERAL_FIELD_CLS} w-full disabled:opacity-60`}>
            <option value="fixed">Fixed</option>
            <option value="floating">Floating</option>
          </select>
        </Field>
        <Field label="Currency">
          <select value={leg.currency} onChange={(e) => onChange((current) => ({ ...current, currency: e.target.value as BilateralCurrency }))} className={`${BILATERAL_FIELD_CLS} w-full`}>
            {CURRENCIES.map((currency) => <option key={currency}>{currency}</option>)}
          </select>
        </Field>
        {leg.priceType === "fixed" ? (
          <div className="col-span-2">
            <Field label={`Fixed price (${leg.currency}/MWh)`}>
              <input type="number" step="0.01" value={leg.fixedPrice} onChange={(e) => onChange((current) => ({ ...current, fixedPrice: e.target.value }))} className={`${BILATERAL_FIELD_CLS} w-full`} />
            </Field>
          </div>
        ) : (
          <>
            <Field label="Floating index">
              <select
                value={leg.indexCode}
                onChange={(e) => {
                  const indexCode = e.target.value;
                  const indexCurrency = indexes.find((index) => index.code === indexCode)?.currency;
                  onChange((current) => ({
                    ...current,
                    indexCode,
                    ...(indexCurrency ? { currency: indexCurrency } : {}),
                  }));
                }}
                className={`${BILATERAL_FIELD_CLS} w-full`}
              >
                <option value="">Select controlled index…</option>
                {indexes.map((index) => <option key={index.code} value={index.code}>{index.label}</option>)}
              </select>
            </Field>
            <Field label={`Spread (${leg.currency}/MWh)`}>
              <input type="number" step="0.01" value={leg.spread} onChange={(e) => onChange((current) => ({ ...current, spread: e.target.value }))} placeholder="0.00" className={`${BILATERAL_FIELD_CLS} w-full`} />
            </Field>
            <Field label="Index multiplier">
              <input
                type="number"
                min="0.000001"
                step="0.000001"
                value={leg.multiplier}
                onChange={(e) => onChange((current) => ({ ...current, multiplier: e.target.value }))}
                placeholder="1.000000"
                className={`${BILATERAL_FIELD_CLS} w-full`}
              />
            </Field>
          </>
        )}
      </div>
    </div>
  );
}

const BLOTTER_PAGE_OPTIONS = [50, 100, 200, 500] as const;
const BLOTTER_PAGE_DEFAULT = 200;

// Page numbers for the pager: all pages when few, otherwise first + last + a
// window around the current page with "…" gaps. `current` and the result are
// 0-based page indices ("ellipsis" marks a gap).
function pageItems(current: number, count: number): (number | "ellipsis")[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i);
  const items: (number | "ellipsis")[] = [0];
  if (current > 2) items.push("ellipsis");
  for (let i = Math.max(1, current - 1); i <= Math.min(count - 2, current + 1); i++) items.push(i);
  if (current < count - 3) items.push("ellipsis");
  items.push(count - 1);
  return items;
}

// "" means "no filter"; the sentinels map to the unbooked/unpaired/no-code flags.
const NO_BOOK = " nobook"; // -> unbooked=true
const NO_PAIR = " nopair"; // -> unpaired=true
const NO_CODE = " nocode"; // -> noTraderCode=true

interface Filters {
  product: string;
  period: string;
  from: string;
  to: string;
  book: string; // "" | NO_BOOK | account name
  pair: string; // "" | NO_PAIR | pair label
  side: "" | "B" | "S";
  trader: string; // "" | uuid | "unassigned"
  traderCode: string; // "" | NO_CODE | eex trader code
  broker: string;
  venue: string;
  source: string;
  origin: "" | "cleared" | "bilateral";
}

const EMPTY_FILTERS: Filters = {
  product: "",
  period: "",
  from: "",
  to: "",
  book: "",
  pair: "",
  side: "",
  trader: "",
  traderCode: "",
  broker: "",
  venue: "",
  source: "",
  origin: "",
};

function Blotter({
  readOnly,
  onEditBilateralDeal,
  focusedTrade,
  companyId,
  wrongCompany,
  invalidFocus,
  onClearFocus,
}: {
  readOnly: boolean;
  onEditBilateralDeal: (dealId: string) => void;
  focusedTrade?: string;
  companyId: string | null;
  wrongCompany: boolean;
  invalidFocus: boolean;
  onClearFocus: () => void;
}) {
  const canManageTrades = useEexCapability("trades.manage");
  const queryClient = useQueryClient();
  const hasTradeFocus = !!focusedTrade || invalidFocus;
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showDeleted, setShowDeleted] = useState(false);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState<number>(BLOTTER_PAGE_DEFAULT);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<BulkEditResult | null>(null);
  const setFilter = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((prev) => ({ ...prev, [k]: v }));

  const debouncedProduct = useDebouncedValue(filters.product.trim());
  const debouncedPeriod = useDebouncedValue(filters.period.trim());
  const debouncedBroker = useDebouncedValue(filters.broker.trim());
  const debouncedVenue = useDebouncedValue(filters.venue.trim());
  const debouncedSource = useDebouncedValue(filters.source.trim());

  // Vocabulary for the filter dropdowns + the bulk "Assign book" control.
  const accountsQ = useQuery({
    queryKey: ["eex-accounts"],
    queryFn: () => api<{ accounts: EexAccount[] }>("/eex/accounts"),
  });
  const labelsQ = useQuery({
    queryKey: ["eex-pair-labels"],
    queryFn: () => api<{ labels: string[] }>("/eex/pair-labels"),
  });
  const accounts = accountsQ.data?.accounts ?? [];
  const activeAccounts = accounts.filter((a) => a.active);
  const labels = labelsQ.data?.labels ?? [];

  // Reset paging + selection when filters change — adjusted during render (not
  // an effect) so the query never sees the intermediate [new filter, old offset].
  const filterKey = JSON.stringify({
    p: debouncedProduct,
    pe: debouncedPeriod,
    f: filters.from,
    t: filters.to,
    b: filters.book,
    pr: filters.pair,
    s: filters.side,
    tr: filters.trader,
    tc: filters.traderCode,
    br: debouncedBroker,
    v: debouncedVenue,
    src: debouncedSource,
    o: filters.origin,
    d: showDeleted,
  });
  const [appliedFilterKey, setAppliedFilterKey] = useState(filterKey);
  if (appliedFilterKey !== filterKey) {
    setAppliedFilterKey(filterKey);
    setOffset(0);
  }

  const params = new URLSearchParams();
  if (debouncedProduct) params.set("product", debouncedProduct);
  if (debouncedPeriod) params.set("period", debouncedPeriod);
  if (filters.from) params.set("tradedFrom", filters.from);
  if (filters.to) params.set("tradedTo", filters.to);
  if (filters.book === NO_BOOK) params.set("unbooked", "true");
  else if (filters.book) params.set("book", filters.book);
  if (filters.pair === NO_PAIR) params.set("unpaired", "true");
  else if (filters.pair) params.set("pair", filters.pair);
  if (filters.side) params.set("side", filters.side);
  if (filters.trader) params.set("trader", filters.trader);
  if (filters.traderCode === NO_CODE) params.set("noTraderCode", "true");
  else if (filters.traderCode) params.set("traderCode", filters.traderCode);
  if (debouncedBroker) params.set("broker", debouncedBroker);
  if (debouncedVenue) params.set("venue", debouncedVenue);
  if (debouncedSource) params.set("source", debouncedSource);
  if (filters.origin) params.set("origin", filters.origin);
  if (showDeleted) params.set("includeDeleted", "true");
  params.set("limit", String(pageSize));
  if (offset > 0) params.set("offset", String(offset));
  if (focusedTrade) {
    // Exact lookup does not inherit a date, product, or pagination restriction.
    for (const key of [...params.keys()]) params.delete(key);
    params.set("id", focusedTrade);
    params.set("limit", "1");
  }

  const tradesQ = useQuery({
    queryKey: ["eex-trades", companyId, focusedTrade, filterKey, offset, pageSize],
    queryFn: () => api<BlotterResult>(`/eex/trades?${params.toString()}`),
    placeholderData: focusedTrade ? undefined : keepPreviousData,
    enabled: !wrongCompany && !invalidFocus,
  });

  const data = wrongCompany || invalidFocus ? undefined : tradesQ.data;

  // Drop selections for rows no longer in the loaded page (paging/filtering).
  useEffect(() => {
    if (!data) return;
    const live = new Set(data.trades.map((t) => t.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [data]);

  // If deletions/privacy shrink the total below the current offset, snap back
  // to the last valid page instead of stranding the user on an empty one.
  useEffect(() => {
    if (data && offset > 0 && offset >= data.total) {
      setOffset(data.total > 0 ? Math.floor((data.total - 1) / pageSize) * pageSize : 0);
    }
  }, [data, offset, pageSize]);

  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS) || showDeleted;

  return (
    <section className="ops-panel ops-blotter space-y-2">
      {hasTradeFocus && (
        <section aria-label="Focused trade" className="rounded-xl border border-accent/25 bg-accent/5 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-accent">Finance · source trade</p>
              <h2 className="text-lg font-semibold">{data?.trades[0] ? `${data.trades[0].product} · ${canonicalPeriod(data.trades[0].period)}` : "Trade detail"}</h2>
              {data?.trades[0] && <p className="text-sm text-muted">
                {data.trades[0].side === "B" ? "Buy" : "Sell"} · Quantity {data.trades[0].quantity} · Price {Number(data.trades[0].price)} {data.trades[0].currency}
                {data.trades[0].book ? ` · ${data.trades[0].book}` : ""} · {data.trades[0].assignedTo}
              </p>}
              <p className="break-all text-xs text-muted">{data?.trades[0]?.tradeId ?? focusedTrade}</p>
            </div>
            <button type="button" className="shrink-0 rounded-lg border border-border bg-bg px-3 py-2 text-sm font-medium hover:border-accent" onClick={onClearFocus}>Show all trades</button>
          </div>
          {wrongCompany && <p role="alert" className="mt-3 text-sm text-warnOrange">This Finance link belongs to another company. Select the original company to open this trade, or show all trades in your current company.</p>}
          {invalidFocus && <p role="alert" className="mt-3 text-sm text-warnOrange">This trade link is incomplete or invalid. Return to Finance and open the source again, or show all trades.</p>}
        </section>
      )}
      {!hasTradeFocus && !readOnly && data?.isAdmin && <ChangeRequestsPanel />}
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-semibold text-sm">
          {hasTradeFocus ? "Trade record and actions" : "Blotter"}
          {tradesQ.isFetching && !tradesQ.isLoading && (
            <span className="ml-2 text-[10px] font-normal text-muted">updating…</span>
          )}
        </h2>
        <div className="mr-auto">
          {data && (readOnly || !data.isAdmin) && data.pendingCount > 0 && (
            <span className="rounded bg-warnOrange/10 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-warnOrange sm:bg-amber-500/15 sm:text-[10px] sm:font-normal sm:text-amber-600 dark:sm:text-amber-400">
              {data.pendingCount} pending
            </span>
          )}
        </div>
        {!hasTradeFocus && !readOnly && data?.isAdmin && (
          <DesktopBlotterActions
            pendingCount={data.pendingCount}
            showDeleted={showDeleted}
            onShowDeletedChange={setShowDeleted}
          />
        )}
      </div>

      {!hasTradeFocus && (readOnly ? (
        <details className="rounded-md border border-border/60">
          <summary className="flex min-h-11 cursor-pointer items-center px-3 text-sm font-medium">
            Filters{hasFilters ? " · active" : ""}
          </summary>
          <div className="border-t border-border/60 p-2">
            <FilterBar
              filters={filters}
              setFilter={setFilter}
              accounts={activeAccounts}
              labels={labels}
              isAdmin={data?.isAdmin ?? false}
              companyId={companyId}
              hasFilters={hasFilters}
              onClear={() => {
                setFilters(EMPTY_FILTERS);
                setShowDeleted(false);
              }}
              onPreset={(preset) => {
                if (preset === "unbooked") setFilters({ ...EMPTY_FILTERS, book: NO_BOOK });
                else setFilters({ ...EMPTY_FILTERS, pair: NO_PAIR });
              }}
              contained={false}
            />
          </div>
        </details>
      ) : (
        <FilterBar
          filters={filters}
          setFilter={setFilter}
          accounts={activeAccounts}
          labels={labels}
          isAdmin={data?.isAdmin ?? false}
          companyId={companyId}
          hasFilters={hasFilters}
          onClear={() => {
            setFilters(EMPTY_FILTERS);
            setShowDeleted(false);
          }}
          onPreset={(preset) => {
            if (preset === "unbooked") setFilters({ ...EMPTY_FILTERS, book: NO_BOOK });
            else setFilters({ ...EMPTY_FILTERS, pair: NO_PAIR });
          }}
        />
      ))}

      {bulkResult && <div role="status" className="text-sm">
        {bulkResultText(bulkResult)}
        {bulkResult.failures?.map((failure) => (
          <div key={failure.trades} className="text-negRed">{failure.trades}: {failure.message}</div>
        ))}
      </div>}
      {!readOnly && canManageTrades && selected.size > 0 && (
        <BulkToolbar
          key={[...selected].sort().join(",")}
          ids={[...selected]}
          selectedTrades={data?.trades.filter(trade => selected.has(trade.id)) ?? []}
          accounts={activeAccounts}
          labels={labels}
          labelsReady={labelsQ.isSuccess}
          onStart={() => setBulkResult(null)}
          onClearSelection={() => setSelected(new Set())}
          onDone={(result) => {
            setBulkResult(result);
            void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
            invalidateEexData(queryClient); // tree/leaf, P&L months, worklist
            void queryClient.invalidateQueries({ queryKey: ["eex-change-requests"] });
            void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
            void queryClient.invalidateQueries({ queryKey: ["eex-margin"] });
            void queryClient.invalidateQueries({ queryKey: ["eex-delivery"] });
            setSelected(new Set());
          }}
        />
      )}

      {!wrongCompany && !invalidFocus && tradesQ.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {tradesQ.isError && <div className="text-negRed text-sm">{(tradesQ.error as Error).message}</div>}
      {data && data.trades.length === 0 && (
        <div role={focusedTrade ? "status" : undefined} className="rounded-lg border border-border px-4 py-3 text-muted text-sm">{focusedTrade ? "This trade is unavailable. It may have been deleted, moved, or be outside your access. No other trade has been substituted." : hasFilters ? "No trades match these filters." : "No trades yet."}</div>
      )}
      {data && data.trades.length > 0 && (
        readOnly ? (
          <CompactTradeList trades={data.trades} />
        ) : (
          <BlotterTable
            data={data}
            accounts={accounts}
            labels={labels}
            selected={selected}
            setSelected={setSelected}
            onEditBilateralDeal={onEditBilateralDeal}
          />
        )
      )}
      {!hasTradeFocus && data && data.total > 0 && (
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <label className="flex items-center gap-1 text-muted">
            Rows
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setOffset(0);
              }}
              className="min-h-11 rounded border border-border/60 bg-transparent px-2 py-2 text-base sm:min-h-0 sm:px-1.5 sm:py-0.5 sm:text-xs"
            >
              {BLOTTER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - pageSize))}
            className="min-h-11 rounded border border-border/60 px-3 py-2 hover:border-accent/60 disabled:opacity-40 sm:min-h-0 sm:px-2 sm:py-0.5"
          >
            ← Prev
          </button>

          {(() => {
            const pageCount = Math.max(1, Math.ceil(data.total / pageSize));
            const current = Math.floor(offset / pageSize);
            return (
              <div className="flex items-center gap-1">
                {pageItems(current, pageCount).map((it, i) =>
                  it === "ellipsis" ? (
                    <span key={`e${i}`} className="px-1 text-muted">
                      …
                    </span>
                  ) : (
                    <button
                      key={it}
                      type="button"
                      disabled={tradesQ.isPlaceholderData}
                      aria-current={it === current ? "page" : undefined}
                      onClick={() => setOffset(it * pageSize)}
                      className={`min-h-11 min-w-11 rounded border px-2 py-2 tabular-nums disabled:opacity-40 sm:min-h-0 sm:min-w-[1.75rem] sm:px-1.5 sm:py-0.5 ${
                        it === current
                          ? "border-accent bg-accent text-white"
                          : "border-border/60 hover:border-accent/60"
                      }`}
                    >
                      {it + 1}
                    </button>
                  )
                )}
              </div>
            );
          })()}

          <button
            type="button"
            disabled={tradesQ.isPlaceholderData || data.offset + data.trades.length >= data.total}
            onClick={() => setOffset(offset + pageSize)}
            className="min-h-11 rounded border border-border/60 px-3 py-2 hover:border-accent/60 disabled:opacity-40 sm:min-h-0 sm:px-2 sm:py-0.5"
          >
            Next →
          </button>

          <span className="ml-1 text-muted tabular-nums">
            {data.offset + 1}–{data.offset + data.trades.length} of {data.total}
          </span>
        </div>
      )}
    </section>
  );
}

function DesktopBlotterActions({
  pendingCount,
  showDeleted,
  onShowDeletedChange,
}: {
  pendingCount: number;
  showDeleted: boolean;
  onShowDeletedChange: (next: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const confirmAll = useMutation({
    mutationFn: () => api<{ confirmed: number }>("/eex/trades/confirm-pending", { method: "POST" }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["eex-trades"] }),
  });

  return (
    <>
      {pendingCount > 0 && (
        <button
          type="button"
          disabled={confirmAll.isPending}
          onClick={() => confirmAll.mutate()}
          className="rounded bg-accent px-3 py-1 text-xs text-white disabled:opacity-50"
        >
          {confirmAll.isPending ? "Confirming…" : `Confirm all pending (${pendingCount})`}
        </button>
      )}
      <label className="flex cursor-pointer items-center gap-1.5 self-center text-xs text-muted">
        <input
          type="checkbox"
          checked={showDeleted}
          onChange={(event) => onShowDeletedChange(event.target.checked)}
        />
        Show deleted
      </label>
    </>
  );
}

function CompactTradeList({ trades }: { trades: StoredTrade[] }) {
  return (
    <ul className="space-y-2" aria-label="EEX trades">
      {trades.map((trade) => {
        const traded = trade.tradedAt
          ? formatTimestamp(trade.tradedAt)
          : trade.raw?.["Date/Time"] ?? "—";
        const status = trade.pendingChange
          ? `${trade.pendingChange} pending`
          : trade.assignmentStatus;

        return (
          <li
            key={trade.id}
            className={cn(
              "rounded-md border border-border/60 bg-bg",
              trade.deletedAt && "opacity-60"
            )}
          >
            <details>
              <summary className="min-h-11 cursor-pointer list-none px-3 py-2.5 marker:hidden [&::-webkit-details-marker]:hidden">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">
                      {trade.product} · <span className="font-mono">{canonicalPeriod(trade.period)}</span>
                    </div>
                    <div className="mt-1 truncate font-mono text-xs text-muted">
                      {trade.tradeId} · {traded}
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-xs">
                    <DirectionLabel signedQty={trade.signedQty} /> MW
                    <div className="mt-1 tabular-nums text-muted">
                      {Number(trade.price)} {trade.currency || "—"}/MWh
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wide">
                  <span className={cn("font-semibold", trade.side === "B" ? "text-posGreen" : "text-negRed")}>{trade.side === "B" ? "Buy" : "Sell"}</span>
                  <span className="rounded bg-border/30 px-1.5 py-0.5 text-fg">{trade.origin}</span>
                  <span className={cn("rounded px-1.5 py-0.5", status === "confirmed" ? "bg-posGreen/10 text-posGreen" : "bg-warnOrange/10 text-warnOrange")}>{status}</span>
                  {trade.deletedAt && <span className="rounded bg-negRed/10 px-1.5 py-0.5 text-negRed">deleted</span>}
                </div>
              </summary>

              <div className="space-y-3 border-t border-border/60 px-3 py-3 text-xs">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <TradeFact label="Traded" value={traded} />
                  <TradeFact label="Venue" value={trade.venue} />
                  <TradeFact label="Quantity" value={`${trade.signedQty} MW`} />
                  <TradeFact label="Price" value={`${Number(trade.price)} ${trade.currency || "—"}/MWh`} />
                  <TradeFact label="Account" value={trade.book} />
                  <TradeFact label="PAIR (grouping only)" value={trade.pairLabel} />
                  <TradeFact label="Assigned trader" value={trade.assignedTo} muted={trade.identityMasked} />
                  <TradeFact label="EEX trader code" value={trade.eexTraderCode} mono />
                  <TradeFact label="Entered by" value={trade.enteredBy} />
                  <TradeFact label="Broker" value={trade.brokerCode} />
                  <TradeFact label="Source" value={trade.source} />
                  <TradeFact label="Paid / given" value={trade.paidGiven} />
                  <TradeFact label="Assignment" value={trade.assignmentStatus} />
                  <TradeFact label="Confirmed" value={trade.assignmentConfirmedAt ? formatTimestamp(trade.assignmentConfirmedAt) : null} />
                </dl>

                {trade.origin === "bilateral" && (
                  <div className="rounded border border-warnOrange/40 bg-warnOrange/10 p-2">
                    <div className="mb-2 font-medium text-warnOrange">Bilateral contract</div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <TradeFact label="Counterparty" value={trade.counterparty} />
                      <TradeFact label="Confirmation" value={trade.contractRef} mono />
                      <TradeFact label="Settlement index" value={trade.settlementIndex} />
                      <TradeFact
                        label="Delivery window"
                        value={trade.deliveryStart && trade.deliveryEnd ? `${trade.deliveryStart} → ${trade.deliveryEnd}` : null}
                        mono
                      />
                    </dl>
                  </div>
                )}

                {trade.origin === "bilateral" ? <BilateralCapturedDetails trade={trade} /> : <details className="rounded border border-border/40">
                  <summary className="flex min-h-11 cursor-pointer items-center px-2 text-xs font-medium">
                    Additional captured fields
                  </summary>
                  <div className="border-t border-border/40 p-2">
                    <RawDetails raw={trade.raw} signedQty={trade.signedQty} />
                  </div>
                </details>}
              </div>
            </details>
          </li>
        );
      })}
    </ul>
  );
}

function TradeFact({
  label,
  value,
  mono = false,
  muted = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wide text-muted">{label}</dt>
      <dd className={cn("break-words", mono && "font-mono", muted && "italic text-muted")}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </dd>
    </div>
  );
}

const SELECT_CLS = "min-h-11 w-full rounded border border-border/60 bg-transparent px-2 py-2 text-base sm:min-h-0 sm:w-auto sm:max-w-[10rem] sm:py-1 sm:text-xs";
const INPUT_CLS = "min-h-11 w-full rounded border border-border/60 bg-transparent px-2 py-2 text-base sm:min-h-0 sm:w-28 sm:py-1 sm:text-xs";
const COMPACT_FILTER_CLS = "min-h-11 w-full rounded border border-border/60 bg-transparent px-2 py-2 text-base sm:min-h-0 sm:w-auto sm:py-1 sm:text-xs";

function FilterBar({
  filters,
  setFilter,
  accounts,
  labels,
  isAdmin,
  companyId,
  hasFilters,
  onClear,
  onPreset,
  contained = true,
}: {
  filters: Filters;
  setFilter: <K extends keyof Filters>(k: K, v: Filters[K]) => void;
  accounts: EexAccount[];
  labels: string[];
  isAdmin: boolean;
  companyId: string | null;
  hasFilters: boolean;
  onClear: () => void;
  onPreset: (preset: "unbooked" | "unpaired") => void;
  contained?: boolean;
}) {
  // Filtering is read-only; non-admin options follow trade visibility rather
  // than relying on the admin-only reassignment directory.
  const userId = useAuth((s) => s.user?.userId);
  const usersQ = useQuery({
    queryKey: ["eex-trades", "filter-traders", companyId, userId, isAdmin],
    enabled: !!companyId,
    queryFn: () => api<{ users: Pick<CompanyUser, "id" | "label">[] }>(isAdmin ? "/eex/users" : "/eex/trade-traders"),
  });
  const users = usersQ.data?.users ?? [];
  const traderCodesQ = useQuery({
    queryKey: ["eex-trader-codes"],
    queryFn: () => api<{ codes: string[] }>("/eex/trader-codes"),
  });
  const traderCodes = traderCodesQ.data?.codes ?? [];

  return (
    <div className={cn("ops-trade-filters space-y-2", contained && "ops-toolbar rounded-md border border-border/60 p-2")}>
      <div className="grid grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-end sm:gap-2">
        <Field label="Product">
          <input
            value={filters.product}
            onChange={(e) => setFilter("product", e.target.value)}
            placeholder="contains…"
            className={INPUT_CLS}
          />
        </Field>
        <Field label="Period">
          <input
            value={filters.period}
            onChange={(e) => setFilter("period", e.target.value)}
            placeholder="contains…"
            className={INPUT_CLS}
          />
        </Field>
        <Field label="From">
          <input type="date" value={filters.from} onChange={(e) => setFilter("from", e.target.value)} className={COMPACT_FILTER_CLS} />
        </Field>
        <Field label="To">
          <input type="date" value={filters.to} onChange={(e) => setFilter("to", e.target.value)} className={COMPACT_FILTER_CLS} />
        </Field>
        <Field label="Book">
          <select value={filters.book} onChange={(e) => setFilter("book", e.target.value)} className={SELECT_CLS}>
            <option value="">All books</option>
            <option value={NO_BOOK}>— (no book) —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.name}>{a.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Pair">
          <select value={filters.pair} onChange={(e) => setFilter("pair", e.target.value)} className={SELECT_CLS}>
            <option value="">All pairs</option>
            <option value={NO_PAIR}>— (unpaired) —</option>
            {labels.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </Field>
        <Field label="Side">
          <select value={filters.side} onChange={(e) => setFilter("side", e.target.value as Filters["side"])} className={COMPACT_FILTER_CLS}>
            <option value="">Both</option>
            <option value="B">Buy</option>
            <option value="S">Sell</option>
          </select>
        </Field>
        <Field label="Trader">
          <select value={filters.trader} onChange={(e) => setFilter("trader", e.target.value)} className={SELECT_CLS}>
            <option value="">All traders</option>
            <option value="unassigned">— (unassigned) —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.label}</option>
            ))}
          </select>
        </Field>
        <Field label="EEX code">
          <select value={filters.traderCode} onChange={(e) => setFilter("traderCode", e.target.value)} className={SELECT_CLS}>
            <option value="">All codes</option>
            <option value={NO_CODE}>— (no code) —</option>
            {traderCodes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Broker">
          <input value={filters.broker} onChange={(e) => setFilter("broker", e.target.value)} placeholder="code" className={INPUT_CLS} />
        </Field>
        <Field label="Venue">
          <input value={filters.venue} onChange={(e) => setFilter("venue", e.target.value)} placeholder="venue" className={INPUT_CLS} />
        </Field>
        <Field label="Source">
          <input value={filters.source} onChange={(e) => setFilter("source", e.target.value)} placeholder="source" className={INPUT_CLS} />
        </Field>
        <Field label="Origin">
          <select
            value={filters.origin}
            onChange={(e) => setFilter("origin", e.target.value as Filters["origin"])}
            className={COMPACT_FILTER_CLS}
          >
            <option value="">All</option>
            <option value="cleared">Cleared</option>
            <option value="bilateral">Bilateral</option>
          </select>
        </Field>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-[10px] uppercase tracking-wide text-muted">Presets:</span>
        <button
          type="button"
          onClick={() => onPreset("unbooked")}
          className={cn(
            "min-h-11 rounded border px-3 py-2 hover:border-accent/60 sm:min-h-0 sm:px-2 sm:py-0.5",
            filters.book === NO_BOOK ? "border-accent text-accent" : "border-border/60"
          )}
        >
          Unbooked
        </button>
        <button
          type="button"
          onClick={() => onPreset("unpaired")}
          className={cn(
            "min-h-11 rounded border px-3 py-2 hover:border-accent/60 sm:min-h-0 sm:px-2 sm:py-0.5",
            filters.pair === NO_PAIR ? "border-accent text-accent" : "border-border/60"
          )}
        >
          Unpaired
        </button>
        {hasFilters && (
          <button type="button" onClick={onClear} className="ml-auto min-h-11 rounded border border-border/60 px-3 py-2 text-muted hover:border-negRed/60 hover:text-negRed sm:min-h-0 sm:px-2 sm:py-0.5">
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

function bulkResultText(result: BulkEditResult) {
  return `${result.applied} updated`
    + (result.pending > 0 ? ` · ${result.pending} pending approval` : "")
    + (result.forbidden > 0 ? ` · ${result.forbidden} not permitted` : "")
    + (result.notFound > 0 ? ` · ${result.notFound} not found` : "")
    + (result.blocked > 0 ? ` · ${result.blocked} blocked` : "")
    + (result.failed ? ` · ${result.failed} failed` : "");
}

function BulkToolbar({
  ids,
  selectedTrades,
  accounts,
  labels,
  labelsReady,
  onStart,
  onClearSelection,
  onDone,
}: {
  ids: string[];
  selectedTrades: Pick<StoredTrade, "id" | "tradeId" | "product" | "period" | "bilateralDealId">[];
  accounts: EexAccount[];
  labels: string[];
  labelsReady: boolean;
  onStart: () => void;
  onClearSelection: () => void;
  onDone: (result: BulkEditResult) => void;
}) {
  const [newPairName, setNewPairName] = useState<string | null>(null);
  const [result, setResult] = useState<BulkEditResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reassignment targets (active traders/admins). WRITE_ROLES-gated, so traders
  // see it too — they propose; admins/free apply. Loaded only while the bar shows.
  const tradersQ = useQuery({
    queryKey: ["eex-assignable-users"],
    queryFn: () => api<{ users: { id: string; label: string }[] }>("/eex/assignable-users"),
  });
  const traders = tradersQ.data?.users ?? [];

  const handlers = {
    onSuccess: (r: BulkEditResult) => {
      setResult(r);
      setError(null);
      onDone(r);
    },
    onError: (e: unknown) => {
      setError(errMessage(e));
      setResult(null);
    },
  };
  // Bilateral PAIR labels belong to the whole deal, not its generated rows.
  // Reuse its atomic allocation route once per deal. Ordinary trade edits keep
  // their existing permission, Recognition confirmation, approval and history.
  const bulkEdit = useMutation({
    mutationFn: async (v: { patch: { book?: string | null; pairLabel?: string | null }; extras?: GateExtras }) => {
      const tradeIds = [...new Set(ids)];
      const byId = new Map(selectedTrades.map(trade => [trade.id, trade]));
      const deals = new Map<string, typeof selectedTrades>();
      const ordinaryIds: string[] = [];
      for (const id of tradeIds) {
        const trade = byId.get(id);
        if (!trade) throw new Error("The selected trades have changed. Reload and select them again.");
        if (v.patch.pairLabel !== undefined && trade.bilateralDealId) {
          const rows = deals.get(trade.bilateralDealId) ?? [];
          rows.push(trade);
          deals.set(trade.bilateralDealId, rows);
        } else {
          ordinaryIds.push(id);
        }
      }
      // Run the Recognition-gated request first. A confirmation refusal or
      // cancellation must happen before any bilateral deal is changed.
      const result: BulkEditResult = ordinaryIds.length > 0
        ? await api<BulkEditResult>("/eex/trades/bulk-edit", {
            method: "POST",
            body: JSON.stringify({ ids: ordinaryIds, patch: v.patch, ...(v.extras ?? {}) }),
          })
        : { total: 0, applied: 0, pending: 0, forbidden: 0, notFound: 0, blocked: 0 };
      result.total = tradeIds.length;
      for (const [dealId, rows] of deals) {
        try {
          await api(`/eex/bilateral-deals/${encodeURIComponent(dealId)}/allocation`, {
            method: "PATCH",
            body: JSON.stringify({ pairLabel: v.patch.pairLabel }),
          });
          result.applied += rows.length;
        } catch (error) {
          // Earlier writes may already have succeeded. Report every outcome and
          // refresh the saved state instead of throwing away partial results.
          result.failed = (result.failed ?? 0) + rows.length;
          (result.failures ??= []).push({
            trades: rows.map(row => row.tradeId).join(", "),
            message: errMessage(error),
          });
        }
      }
      return result;
    },
    ...handlers,
  });
  const bulkReassign = useMutation({
    mutationFn: (assignedToUserId: string) =>
      api<BulkEditResult>("/eex/trades/bulk-reassign", {
        method: "POST",
        body: JSON.stringify({ ids, assignedToUserId }),
      }),
    ...handlers,
  });
  const busy = bulkEdit.isPending || bulkReassign.isPending;
  // Reset any prior result/error before kicking off a new bulk action.
  const run = (fn: () => void) => {
    onStart();
    setResult(null);
    setError(null);
    fn();
  };
  const selectClass =
    "bg-transparent border border-border/60 rounded px-2 py-1 text-xs disabled:opacity-50";

  return (
    <div className="sticky top-0 z-10 flex items-center gap-3 flex-wrap border border-accent/60 bg-accent/5 rounded-md px-3 py-2 text-xs">
      <span className="font-semibold">{ids.length} selected</span>

      {selectedTrades.some(trade => trade.bilateralDealId) && (
        <span className="w-full text-muted">Pair changes apply to each entire bilateral deal, including its unselected rows.</span>
      )}

      <label className="flex items-center gap-1.5">
        <span className="text-muted">Assign book</span>
        <select
          value=""
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value;
            e.currentTarget.value = "";
            if (v) run(() => void withRecognitionConfirm((x) => bulkEdit.mutateAsync({ patch: { book: v === " clear" ? null : v }, extras: x })).catch(() => {}));
          }}
          className={selectClass}
        >
          <option value="">{busy ? "Applying…" : "choose…"}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.name}>{a.name}</option>
          ))}
          <option value=" clear">— clear book —</option>
        </select>
      </label>

      <label className="flex items-center gap-1.5">
        <span className="text-muted">Assign pair</span>
        <select
          value=""
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value;
            e.currentTarget.value = "";
            if (v) run(() => void withRecognitionConfirm((x) => bulkEdit.mutateAsync({ patch: { pairLabel: v === " clear" ? null : v }, extras: x })).catch(() => {}));
          }}
          className={selectClass}
        >
          <option value="">{busy ? "Applying…" : "choose…"}</option>
          {labels.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
          <option value=" clear">— clear pair —</option>
        </select>
      </label>

      <button type="button" disabled={busy || !labelsReady} aria-haspopup="dialog"
        onClick={() => run(() => setNewPairName(suggestPairName(selectedTrades, labels)))}
        className="rounded border border-accent/60 px-3 py-1 font-medium text-accent hover:bg-accent/10 disabled:opacity-50">
        Create new pair using these trades
      </button>
      {newPairName !== null && <CreatePairDialog
        suggestedName={newPairName} labels={labels} count={ids.length} error={error}
        includesBilateralDeals={selectedTrades.some(trade => trade.bilateralDealId)}
        onClose={() => { setNewPairName(null); setError(null); }}
        onCreate={async name => {
          setError(null);
          try {
            const response = await withRecognitionConfirm(extras => bulkEdit.mutateAsync({ patch: { pairLabel: name }, extras }));
            if (response === null) setError(null);
          } catch { /* The mutation exposes the error in the dialog for retry. */ }
        }}
      />}

      <label className="flex items-center gap-1.5">
        <span className="text-muted">Assign trader</span>
        <select
          value=""
          disabled={busy || tradersQ.isLoading}
          onChange={(e) => {
            const v = e.target.value;
            e.currentTarget.value = "";
            if (v) run(() => bulkReassign.mutate(v));
          }}
          className={selectClass}
        >
          <option value="">
            {busy ? "Applying…" : tradersQ.isLoading ? "loading…" : "choose…"}
          </option>
          {traders.map((u) => (
            <option key={u.id} value={u.id}>{u.label}</option>
          ))}
        </select>
      </label>

      <button type="button" onClick={onClearSelection} className="text-muted hover:text-fg underline">
        Clear selection
      </button>
      {result && (
        <span className="ml-auto text-fg">
          {result.applied} updated
          {result.pending > 0 && ` · ${result.pending} pending approval`}
          {result.forbidden > 0 && ` · ${result.forbidden} not permitted`}
          {result.notFound > 0 && ` · ${result.notFound} not found`}
          {result.blocked > 0 && ` · ${result.blocked} blocked`}
        </span>
      )}
      {error && <span className="ml-auto text-negRed">{error}</span>}
    </div>
  );
}

function ChangeRequestsPanel() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(null);
  const [processed, setProcessed] = useState(0);
  const [batchResult, setBatchResult] = useState<{
    action: "approve" | "reject";
    succeeded: number;
    failed: { id: string; tradeId: string; message: string }[];
  } | null>(null);
  const reqQ = useQuery({
    queryKey: ["eex-change-requests"],
    queryFn: () => api<{ requests: ChangeRequest[] }>("/eex/trades/change-requests"),
  });
  const act = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject" }) =>
      api(`/eex/trades/change-requests/${v.id}/${v.action}`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["eex-change-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
      invalidateEexData(queryClient); // tree/leaf, P&L months, worklist
      // an approved edit/delete changes the VM ledger replay, delivery
      // positions and open remainders
      void queryClient.invalidateQueries({ queryKey: ["eex-margin"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-delivery"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
    },
  });
  const requests = reqQ.data?.requests ?? [];
  const requestIds = requests.map((r) => r.id);
  const requestKey = requestIds.join(",");
  useEffect(() => {
    const live = new Set(requestIds);
    setSelected((previous) => {
      const next = new Set([...previous].filter((id) => live.has(id)));
      return next.size === previous.size ? previous : next;
    });
  // A refreshed request list removes completed or withdrawn requests from selection.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);
  const selectedRequests = requests.filter((r) => selected.has(r.id));
  const allSelected = requests.length > 0 && selectedRequests.length === requests.length;
  const seenTrades = new Set<string>();
  const duplicateTrades = new Set<string>();
  for (const request of selectedRequests) {
    if (seenTrades.has(request.tradeId)) duplicateTrades.add(request.tradeId);
    else seenTrades.add(request.tradeId);
  }
  const approvalFields = new Map<string, Set<string>>();
  const approvalConflicts = new Set<string>();
  for (const request of selectedRequests) {
    const proposedFields = Object.keys(request.proposed ?? {});
    const fields = request.type === "edit"
      ? proposedFields.length > 0 ? proposedFields : ["*"]
      : request.type === "reassign" ? ["assignedToUserId"] : ["*"];
    const current = approvalFields.get(request.tradeId) ?? new Set<string>();
    if (current.size > 0 && (current.has("*") || fields.includes("*") || fields.some((field) => current.has(field)))) {
      approvalConflicts.add(request.tradeId);
    }
    fields.forEach((field) => current.add(field));
    approvalFields.set(request.tradeId, current);
  }
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["eex-change-requests"] });
    void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
    invalidateEexData(queryClient);
    void queryClient.invalidateQueries({ queryKey: ["eex-margin"] });
    void queryClient.invalidateQueries({ queryKey: ["eex-delivery"] });
    void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
  };
  const batch = useMutation({
    mutationFn: async (action: "approve" | "reject") => {
      const failed: { id: string; tradeId: string; message: string }[] = [];
      let succeeded = 0;
      setProcessed(0);
      // The existing endpoint rechecks permissions, trade state and Recognition
      // for every request at review time. Keep its per-request failure boundary.
      for (const [index, request] of selectedRequests.entries()) {
        try {
          await api(`/eex/trades/change-requests/${request.id}/${action}`, { method: "POST" });
          succeeded++;
        } catch (error) {
          failed.push({ id: request.id, tradeId: request.tradeId, message: errMessage(error) });
        }
        setProcessed(index + 1);
      }
      return { action, succeeded, failed };
    },
    onSuccess: (result) => {
      setBatchResult(result);
      setSelected(new Set(result.failed.map((failure) => failure.id)));
      setReviewAction(null);
      refresh();
    },
  });
  if (requests.length === 0 && !batchResult) return null;

  return (
    <div className="space-y-2 rounded-md border border-amber-500/50 bg-amber-500/5 p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">
          Pending change requests ({requests.length})
        </div>
        {requests.length > 0 && <label className="flex items-center gap-1.5 text-xs">
          <input type="checkbox" aria-label="Select all pending change requests"
            checked={allSelected}
            ref={(element) => { if (element) element.indeterminate = selected.size > 0 && !allSelected; }}
            disabled={batch.isPending || act.isPending}
            onChange={(event) => setSelected(event.target.checked ? new Set(requestIds) : new Set())} />
          Select all
        </label>}
        {selectedRequests.length > 0 && <div className="flex items-center gap-2 text-xs">
          <span>{selectedRequests.length} selected</span>
          <button type="button" disabled={batch.isPending || act.isPending}
            onClick={() => { setBatchResult(null); setReviewAction("approve"); }}
            className="text-posGreen hover:underline disabled:opacity-50">Approve selected</button>
          <button type="button" disabled={batch.isPending || act.isPending}
            onClick={() => { setBatchResult(null); setReviewAction("reject"); }}
            className="text-negRed hover:underline disabled:opacity-50">Reject selected</button>
          <button type="button" disabled={batch.isPending || act.isPending}
            onClick={() => { setSelected(new Set()); setReviewAction(null); }}
            className="text-muted hover:underline disabled:opacity-50">Clear selection</button>
        </div>}
      </div>
      {reviewAction && selectedRequests.length > 0 && <div role="group" aria-label={`Review ${reviewAction} selected requests`}
        className="space-y-2 rounded border border-amber-500/50 bg-bg p-2 text-xs">
        <p>Review {selectedRequests.length} request{selectedRequests.length === 1 ? "" : "s"} to {reviewAction}. Each is checked separately; failed requests remain selected if still pending.</p>
        {duplicateTrades.size > 0 && <p className="text-warnOrange">
          Multiple requests affect {[...duplicateTrades].join(", ")}. They will be processed in the order shown below, so later changes may replace earlier ones.
        </p>}
        {reviewAction === "approve" && approvalConflicts.size > 0 && <p role="alert" className="text-negRed">
          Select only one request per field for {[...approvalConflicts].join(", ")} before approving. These requests would overwrite each other.
        </p>}
        <ol className="list-decimal pl-5 max-h-32 overflow-auto">
          {selectedRequests.map((r) => <li key={r.id}>
            {r.tradeId} · {r.type} {r.type === "edit" && r.proposed ? <ProposedDiff proposed={r.proposed} /> : r.type === "reassign" ? `→ ${r.reassignToLabel ?? "—"}` : ""}
          </li>)}
        </ol>
        <div className="flex items-center gap-3">
          <button type="button" disabled={batch.isPending || (reviewAction === "approve" && approvalConflicts.size > 0)} onClick={() => batch.mutate(reviewAction)}
            className={reviewAction === "approve" ? "font-semibold text-posGreen hover:underline disabled:opacity-50" : "font-semibold text-negRed hover:underline disabled:opacity-50"}>
            {batch.isPending ? `Processing ${processed}/${selectedRequests.length}…` : `${reviewAction === "approve" ? "Confirm approval" : "Confirm rejection"} of ${selectedRequests.length}`}
          </button>
          <button type="button" disabled={batch.isPending} onClick={() => setReviewAction(null)} className="text-muted hover:underline disabled:opacity-50">Cancel</button>
        </div>
      </div>}
      {batchResult && <div role="status" className="text-xs">
        {batchResult.succeeded} {batchResult.action === "approve" ? "approved" : "rejected"}
        {batchResult.failed.length > 0 && ` · ${batchResult.failed.length} failed`}
        {batchResult.failed.map((failure) => <p key={failure.id} className="text-negRed">{failure.tradeId}: {failure.message}</p>)}
      </div>}
      {act.isError && <p role="alert" className="text-xs text-negRed">{errMessage(act.error)}</p>}
      <div className="space-y-1">
        {requests.map((r) => (
          <div key={r.id} className="flex items-center gap-3 text-xs flex-wrap">
            <input type="checkbox" aria-label={`Select request for trade ${r.tradeId}: ${r.type} ${r.type === "edit" ? JSON.stringify(r.proposed) : r.reassignToLabel ?? ""}`}
              checked={selected.has(r.id)} disabled={batch.isPending || act.isPending}
              onChange={(event) => setSelected((previous) => {
                const next = new Set(previous);
                if (event.target.checked) next.add(r.id); else next.delete(r.id);
                return next;
              })} />
            <span className={cn("rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide", r.type === "delete" ? "bg-negRed/15 text-negRed" : r.type === "reassign" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-accent/15 text-accent")}>
              {r.type}
            </span>
            <span className="font-mono">{r.tradeId}</span>
            <span className="text-muted">
              {r.product} · {canonicalPeriod(r.period)} · {r.side}{r.signedQty} @ {Number(r.price)}
            </span>
            {r.type === "edit" && r.proposed && <ProposedDiff proposed={r.proposed} />}
            {r.type === "reassign" && (
              <span className="text-fg">→ reassign to {r.reassignToLabel ?? "—"}</span>
            )}
            <span className="text-muted ml-auto">
              by {r.requestedByName || r.requestedByEmail || "—"}
            </span>
            <button type="button" disabled={act.isPending || batch.isPending} onClick={() => act.mutate({ id: r.id, action: "approve" })} className="text-posGreen hover:underline disabled:opacity-50">
              Approve
            </button>
            <button type="button" disabled={act.isPending || batch.isPending} onClick={() => act.mutate({ id: r.id, action: "reject" })} className="text-negRed hover:underline disabled:opacity-50">
              Reject
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposedDiff({ proposed }: { proposed: Record<string, unknown> }) {
  // Requests now store a DIFFED patch (only what changes), so every present key
  // IS the change — including pairLabel and explicit null-clears (venue,
  // tradedAt, paidGiven, book, pairLabel can all be intentionally cleared;
  // render those as "k=∅" instead of hiding the request's only content).
  const keys = ["price", "quantity", "side", "product", "period", "tradedAt", "venue", "paidGiven", "book", "pairLabel"];
  const parts = keys
    .filter((k) => proposed[k] !== undefined)
    .map((k) => `${k}=${proposed[k] === null ? "∅" : k === "period" ? canonicalPeriod(String(proposed[k])) : String(proposed[k])}`);
  return <span className="text-fg">→ {parts.join(", ")}</span>;
}

function BlotterTable({
  data,
  accounts,
  labels,
  selected,
  setSelected,
  onEditBilateralDeal,
}: {
  data: BlotterResult;
  accounts: EexAccount[];
  labels: string[];
  selected: Set<string>;
  setSelected: Dispatch<SetStateAction<Set<string>>>;
  onEditBilateralDeal: (dealId: string) => void;
}) {
  const queryClient = useQueryClient();
  const role = useAuth((s) => s.user?.role);
  const canManage = useEexCapability("trades.manage");
  const canWrite = (role === "company_admin" || role === "trader") && canManage;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Anchor by trade ID so a refetch/re-sort cannot move it to another row.
  const lastClicked = useRef<string | null>(null);
  const toggle = (k: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const rows = canManage ? data.trades : data.trades.map(trade => ({ ...trade,
    canEdit: false, canEditPrivacy: false, canEditAllocation: false, canEditBilateralDeal: false }));
  const allSelected = rows.length > 0 && rows.every((t) => selected.has(t.id));
  const someSelected = rows.some((t) => selected.has(t.id));

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) for (const t of rows) next.delete(t.id);
      else for (const t of rows) next.add(t.id);
      return next;
    });

  const onRowSelect = (index: number, shiftKey: boolean) => {
    const clickedId = rows[index]?.id;
    if (clickedId == null) return;
    // Capture the previous anchor and current row order before queueing the
    // React update. Reading the ref inside the updater is timing-dependent:
    // the ref below may already point at clickedId by the time React runs it.
    const updateSelection = createTradeSelectionUpdater(
      rows.map((row) => row.id),
      clickedId,
      lastClicked.current,
      shiftKey
    );
    lastClicked.current = clickedId;
    setSelected(updateSelection);
  };

  const companyUsersQ = useQuery({
    queryKey: ["eex-company-users"],
    enabled: data.isAdmin,
    queryFn: () => api<{ users: CompanyUser[] }>("/eex/users"),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
  const confirmOne = useMutation({
    mutationFn: (id: string) => api(`/eex/trades/${id}/confirm`, { method: "POST" }),
    onSuccess: invalidate,
  });
  // Non-admins can only PROPOSE a reassignment (-> pending change-request unless
  // their permission is 'free'); they pick from a lightweight user list.
  const assignableUsersQ = useQuery({
    queryKey: ["eex-assignable-users"],
    // Only write-roles can reassign; risk/viewer would get a 403 here.
    enabled: !data.isAdmin && canWrite,
    queryFn: () => api<{ users: { id: string; label: string }[] }>("/eex/assignable-users"),
  });
  const requestReassign = useMutation({
    mutationFn: (v: { id: string; assignedToUserId: string }) =>
      api(`/eex/trades/${v.id}/request-reassign`, {
        method: "POST",
        body: JSON.stringify({ assignedToUserId: v.assignedToUserId }),
      }),
    onSuccess: invalidate,
  });
  const setPrivacy = useMutation({
    mutationFn: (v: { id: string; hideAttribution?: boolean; isPrivate?: boolean }) =>
      api(`/eex/trades/${v.id}/privacy`, {
        method: "PATCH",
        body: JSON.stringify({ hideAttribution: v.hideAttribution, isPrivate: v.isPrivate }),
      }),
    onSuccess: invalidate,
  });

  const [allocError, setAllocError] = useState<string | null>(null);
  const [allocNotice, setAllocNotice] = useState<string | null>(null);
  const bookWritesInFlight = useRef(new Set<string>());
  const [savingBooks, setSavingBooks] = useState<Map<string, string | null>>(new Map());
  const patchAllocation = useMutation({
    // Gated (label unification): a book/pair change re-buckets matching, so the
    // server may demand the WARNING confirm+reason flow — and hard refusals
    // (locked month, admin-only, active declared close) must be SEEN, not
    // swallowed.
    mutationFn: (v: { id: string; book?: string | null; pairLabel?: string | null; extras?: GateExtras }) =>
      api<{ pending: boolean }>(`/eex/trades/${v.id}/allocation`, {
        method: "PATCH",
        body: JSON.stringify({ book: v.book, pairLabel: v.pairLabel, ...(v.extras ?? {}) }),
      }),
    onError: (e) => { setAllocNotice(null); setAllocError(errMessage(e)); },
    onSuccess: async (result, variables) => {
      setAllocError(null);
      setAllocNotice(result.pending ? "Account change submitted for admin approval." : null);
      if (!result.pending && variables.book !== undefined) {
        const savedBook = variables.book;
        // A later row's save can replace this row's in-flight refresh. Keep the
        // acknowledged value visible while that newer authoritative read loads.
        queryClient.setQueriesData<BlotterResult>({ queryKey: ["eex-trades"] }, current => !Array.isArray(current?.trades) ? current : ({
          ...current,
          trades: current.trades.map(trade => trade.id === variables.id ? { ...trade, book: savedBook } : trade),
        }));
      }
      invalidateEexData(queryClient); // tree/leaf, P&L months, worklist
      void queryClient.invalidateQueries({ queryKey: ["eex-pair-labels"] });
      // moving a trade between accounts changes which pairs can allocate it
      void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
      // Join the trade refresh already started above instead of cancelling it.
      await queryClient.invalidateQueries({ queryKey: ["eex-trades"] }, { cancelRefetch: false });
    },
  });
  const saveBook = (id: string, book: string | null) => {
    // Keep the row busy through confirmation and the authoritative refetch.
    // A ref also prevents duplicate events before React paints the disabled control.
    if (bookWritesInFlight.current.has(id)) return;
    bookWritesInFlight.current.add(id);
    setSavingBooks(previous => new Map(previous).set(id, book));
    setAllocError(null);
    setAllocNotice(null);
    void withRecognitionConfirm(extras => patchAllocation.mutateAsync({ id, book, extras }))
      .catch(() => {}) // The mutation's error handler displays the refusal.
      .finally(() => {
        bookWritesInFlight.current.delete(id);
        setSavingBooks(previous => {
          const next = new Map(previous);
          next.delete(id);
          return next;
        });
      });
  };
  const patchBilateralAllocation = useMutation({
    mutationFn: (v: { dealId: string; book?: string | null; pairLabel?: string | null }) =>
      api(`/eex/bilateral-deals/${encodeURIComponent(v.dealId)}/allocation`, {
        method: "PATCH",
        body: JSON.stringify({ book: v.book, pairLabel: v.pairLabel }),
      }),
    onError: (e) => setAllocError(errMessage(e)),
    onSuccess: () => {
      setAllocError(null);
      void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
      invalidateEexData(queryClient); // tree/leaf and bilateral display-only valuation
      void queryClient.invalidateQueries({ queryKey: ["eex-pair-labels"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deals"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-bilateral-deal"] });
    },
  });

  const COLS = 17; // select, expand, Traded, Trade ID, Product, Period, Origin, Account, Pair, Side, Qty, Price, Currency, Trader, EEX code, Status (+admin action)

  return (
    <div className="ops-table-frame overflow-auto border border-border/60 rounded-md max-h-[calc(100vh-14rem)]">
      {allocError && (
        <div className="px-2 py-1 text-xs text-negRed border-b border-border/40">{allocError}</div>
      )}
      {allocNotice && (
        <div role="status" className="px-2 py-1 text-xs text-muted border-b border-border/40">{allocNotice}</div>
      )}
      <datalist id="eex-pair-labels">
        {labels.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
      <table className="min-w-full text-xs">
        <thead className="bg-bg sticky top-0 text-muted">
          <tr>
            <Th className="w-8 text-center">
              <input
                type="checkbox"
                aria-label="Select all loaded rows"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelected && someSelected;
                }}
                onChange={toggleAll}
              />
            </Th>
            <Th />
            <Th>Traded</Th>
            <Th>Trade ID</Th>
            <Th>Product</Th>
            <Th>Period</Th>
            <Th>Origin</Th>
            <Th>Account</Th>
            <Th>Pair</Th>
            <Th className="text-center">Side</Th>
            <Th className="text-right">Qty</Th>
            <Th className="text-right">Price</Th>
            <Th>Currency</Th>
            <Th>Trader</Th>
            <Th>EEX code</Th>
            <Th>Status</Th>
            {data.isAdmin && <Th />}
          </tr>
        </thead>
        <tbody>
          {data.trades.map((t, index) => {
            const open = expanded.has(t.id);
            const isSelected = selected.has(t.id);
            const traded = t.tradedAt ? formatTimestamp(t.tradedAt) : t.raw?.["Date/Time"] ?? "—";
            return (
              <Fragment key={t.id}>
                <tr className={cn("border-t border-border/30 hover:bg-border/10", t.deletedAt && "opacity-50", isSelected && "bg-accent/5")}>
                  <Td className="text-center">
                    <input
                      type="checkbox"
                      aria-label={`Select trade ${t.tradeId}`}
                      checked={isSelected}
                      // shift-click selects a range; native checkboxes expose the
                      // modifier on the click event, so read it there.
                      onClick={(e) => onRowSelect(index, e.shiftKey)}
                      onChange={() => {}}
                    />
                  </Td>
                  <Td>
                    <button type="button" onClick={() => toggle(t.id)} aria-label="Toggle details" className="text-muted hover:text-fg w-4">
                      {open ? "▾" : "▸"}
                    </button>
                  </Td>
                  <Td>{traded}</Td>
                  <Td className="font-mono">
                    {t.tradeId}
                    {t.deletedAt && <span className="ml-1 text-negRed text-[10px] uppercase tracking-wide">deleted</span>}
                  </Td>
                  <Td>{t.product}</Td>
                  <Td className="font-mono">{canonicalPeriod(t.period)}</Td>
                  <Td>
                    {t.origin === "bilateral" && (
                      <span
                        title={`Bilateral (ISDA/OTC)${t.counterparty ? ` — ${t.counterparty}` : ""} — never on the clearing-bank statement`}
                        className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400"
                      >
                        BIL
                      </span>
                    )}
                  </Td>
                  <Td>
                    {t.bilateralDealId ? (
                      t.canEditBilateralDeal ? (
                        <select
                          aria-label={`Book assignment for ${t.tradeId}`}
                          value={t.book ?? ""}
                          disabled={patchBilateralAllocation.isPending}
                          title="Updates Book on the whole bilateral deal"
                          onChange={(e) => {
                            const book = e.target.value || null;
                            patchBilateralAllocation.mutate({
                              dealId: t.bilateralDealId!,
                              book,
                            });
                          }}
                          className="bg-transparent border border-border/40 rounded px-1 py-0.5 text-xs max-w-[8rem] disabled:opacity-50"
                        >
                          <option value="">—</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.name}>
                              {a.name}
                            </option>
                          ))}
                          {t.book && !accounts.some((a) => a.name.toLowerCase() === t.book!.toLowerCase()) && (
                            <option value={t.book}>{t.book}</option>
                          )}
                        </select>
                      ) : (
                        <span>{t.book ?? "—"}</span>
                      )
                    ) : t.canEditAllocation ? (
                      <div className="flex items-center gap-1.5" aria-busy={savingBooks.has(t.id)}>
                        <select
                          aria-label={`Book assignment for ${t.tradeId}`}
                          value={(savingBooks.has(t.id) ? savingBooks.get(t.id) : t.book) ?? ""}
                          disabled={savingBooks.has(t.id)}
                          title={!data.isAdmin && t.book ? "Changing this account requires admin approval" : "Assign account"}
                          onChange={(e) => {
                            saveBook(t.id, e.target.value || null);
                          }}
                          className="bg-transparent border border-border/40 rounded px-1 py-0.5 text-xs max-w-[8rem] disabled:opacity-50"
                        >
                          <option value="">—</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.name}>
                              {a.name}
                            </option>
                          ))}
                          {t.book && !accounts.some((a) => a.name.toLowerCase() === t.book!.toLowerCase()) && (
                            <option value={t.book}>{t.book}</option>
                          )}
                        </select>
                        {savingBooks.has(t.id) && <span role="status" className="text-[10px] text-muted whitespace-nowrap">Saving…</span>}
                      </div>
                    ) : (
                      <span>{t.book ?? "—"}</span>
                    )}
                  </Td>
                  <Td>
                    {t.bilateralDealId ? (
                      t.canEditBilateralDeal ? (
                        <input
                          aria-label={`PAIR label for ${t.tradeId}`}
                          key={`${t.bilateralDealId}:${t.pairLabel ?? ""}`}
                          list="eex-pair-labels"
                          defaultValue={t.pairLabel ?? ""}
                          disabled={patchBilateralAllocation.isPending}
                          title="Updates grouping-only PAIR on the whole bilateral deal"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          onBlur={(e) => {
                            const pairLabel = e.currentTarget.value.trim() || null;
                            if (pairLabel !== (t.pairLabel ?? null)) {
                              patchBilateralAllocation.mutate({
                                dealId: t.bilateralDealId!,
                                pairLabel,
                              });
                            }
                          }}
                          className="bg-transparent border border-border/40 rounded px-1 py-0.5 text-xs w-24 disabled:opacity-50"
                        />
                      ) : (
                        <span>{t.pairLabel ?? "—"}</span>
                      )
                    ) : t.canEditAllocation ? (
                      <input
                        aria-label={`PAIR label for ${t.tradeId}`}
                        list="eex-pair-labels"
                        defaultValue={t.pairLabel ?? ""}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                        onBlur={(e) => {
                          const next = e.currentTarget.value.trim() || null;
                          if (next !== (t.pairLabel ?? null))
                            void withRecognitionConfirm((x) =>
                              patchAllocation.mutateAsync({ id: t.id, pairLabel: next, extras: x })
                            ).catch(() => {});
                        }}
                        className="bg-transparent border border-border/40 rounded px-1 py-0.5 text-xs w-24"
                      />
                    ) : (
                      <span>{t.pairLabel ?? "—"}</span>
                    )}
                  </Td>
                  <Td className={cn("text-center font-semibold", t.side === "B" ? "text-posGreen" : "text-negRed")}>{t.side}</Td>
                  <Td className="text-right tabular-nums">{t.signedQty}</Td>
                  <Td className="text-right tabular-nums">{Number(t.price)}</Td>
                  <Td className="font-mono">{t.currency || "—"}</Td>
                  <Td>
                    <span className={cn(t.identityMasked && "text-muted italic")}>{t.assignedTo}</span>
                    {t.isPrivate && <span title="Hidden from other traders" className="ml-1 text-muted">🔒</span>}
                  </Td>
                  <Td>
                    {t.eexTraderCode ? (
                      <span className="font-mono text-[11px] text-muted" title="EEX execution trader (from the confirmation)">{t.eexTraderCode}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </Td>
                  <Td>
                    <StatusBadge status={t.assignmentStatus} />
                    {t.pendingChange && (
                      <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                        {t.pendingChange} pending
                      </span>
                    )}
                  </Td>
                  {data.isAdmin && (
                    <Td>
                      {t.assignmentStatus === "pending" && (
                        <button
                          type="button"
                          disabled={confirmOne.isPending}
                          onClick={() => confirmOne.mutate(t.id)}
                          className="text-accent hover:underline disabled:opacity-50"
                        >
                          Confirm
                        </button>
                      )}
                    </Td>
                  )}
                </tr>
                {open && (
                  <tr className="bg-border/5">
                    <td colSpan={data.isAdmin ? COLS : COLS - 1} className="px-4 py-3 space-y-3">
                      {t.bilateralDealId && (
                        <div className="flex items-center gap-3 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 text-xs">
                          <span className="text-muted">
                            This blotter row comes from the bilateral deal. Amend Book, PAIR, terms, and legs on that deal.
                          </span>
                          {t.canEditBilateralDeal && (
                            <button
                              type="button"
                              onClick={() => onEditBilateralDeal(t.bilateralDealId!)}
                              className="ml-auto whitespace-nowrap rounded border border-accent/50 px-2 py-1 text-accent hover:bg-accent/10"
                            >
                              Edit bilateral deal
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-8 gap-y-1 text-xs">
                        <span>
                          <span className="text-muted">Entered by:</span> {t.enteredBy}
                        </span>
                        {t.origin === "bilateral" && (
                          <>
                            <span>
                              <span className="text-muted">Counterparty:</span>{" "}
                              <span className="font-mono">{t.counterparty ?? "—"}</span>
                            </span>
                            {t.contractRef && (
                              <span>
                                <span className="text-muted">Confirmation:</span>{" "}
                                <span className="font-mono">{t.contractRef}</span>
                              </span>
                            )}
                            {t.settlementIndex && (
                              <span>
                                <span className="text-muted">Settlement index:</span>{" "}
                                {t.settlementIndex}
                              </span>
                            )}
                            {t.deliveryStart && t.deliveryEnd && (
                              <span>
                                <span className="text-muted">Delivery window:</span>{" "}
                                <span className="font-mono">
                                  {t.deliveryStart} → {t.deliveryEnd}
                                </span>
                              </span>
                            )}
                          </>
                        )}
                        <span>
                          <span className="text-muted">EEX trader code:</span>{" "}
                          {t.eexTraderCode ? (
                            <span className="font-mono">{t.eexTraderCode}</span>
                          ) : (
                            <span className="text-muted">— (our side not identified)</span>
                          )}
                        </span>
                        {t.assignmentConfirmedAt && (
                          <span>
                            <span className="text-muted">Confirmed:</span> {formatTimestamp(t.assignmentConfirmedAt)}
                          </span>
                        )}
                      </div>

                      {data.isAdmin && <TradeAssignment trade={t} users={companyUsersQ.data?.users} />}

                      {!data.isAdmin && t.canEdit && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted">Request reassignment to:</span>
                          <select
                            value=""
                            disabled={t.pendingChange === "reassign" || requestReassign.isPending}
                            onChange={(e) => {
                              if (e.target.value) requestReassign.mutate({ id: t.id, assignedToUserId: e.target.value });
                            }}
                            className="bg-transparent border border-border/60 rounded px-2 py-1 disabled:opacity-50"
                          >
                            <option value="">choose trader…</option>
                            {(assignableUsersQ.data?.users ?? []).map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.label}
                              </option>
                            ))}
                          </select>
                          {t.pendingChange === "reassign" ? (
                            <span className="text-amber-600 dark:text-amber-400">requested — pending approval</span>
                          ) : t.editNeedsApproval ? (
                            <span className="text-muted">(needs admin approval)</span>
                          ) : (
                            <span className="text-muted">(applies immediately)</span>
                          )}
                        </div>
                      )}

                      {t.canEditPrivacy && (
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
                          <span className="text-muted">Privacy:</span>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={t.hideAttribution}
                              disabled={setPrivacy.isPending}
                              onChange={(e) => setPrivacy.mutate({ id: t.id, hideAttribution: e.target.checked })}
                            />
                            Hide name
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={t.isPrivate}
                              disabled={setPrivacy.isPending}
                              onChange={(e) => setPrivacy.mutate({ id: t.id, isPrivate: e.target.checked })}
                            />
                            Hide trade from others
                          </label>
                        </div>
                      )}

                      {t.canEdit && <TradeEditForm trade={t} />}
                      <TradeHistory tradeId={t.id} />
                      {t.origin === "bilateral" ? <BilateralCapturedDetails trade={t} /> : <RawDetails raw={t.raw} signedQty={t.signedQty} />}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className="px-2 py-1 text-[10px] text-muted border-t border-border/30 sticky bottom-0 bg-bg">
        showing {data.offset + 1}–{data.offset + data.trades.length} of {data.total} · click ▸ for
        entered-by, edit/delete, history, privacy and full captured detail
      </div>
    </div>
  );
}

// Our own derived position as a signed quantity (+ bought / − sold), color-coded
// — distinct from the platform's Initiator/Aggressor actions.
function DirectionLabel({ signedQty }: { signedQty: number }) {
  const buy = signedQty > 0;
  return (
    <span className={cn("font-semibold tabular-nums", buy ? "text-posGreen" : "text-negRed")}>
      {buy ? `+${signedQty}` : signedQty}
    </span>
  );
}

// Preview table for not-yet-saved parsed rows (no assignment/status yet).
function PreviewTable({ rows }: { rows: ParsedTrade[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  return (
    <div className="overflow-auto border border-border/60 rounded-md">
      <table className="min-w-full text-xs">
        <thead className="bg-bg sticky top-0 text-muted">
          <tr>
            <Th />
            <Th>Traded</Th>
            <Th>Trade ID</Th>
            <Th>Venue</Th>
            <Th>Product</Th>
            <Th>Period</Th>
            <Th>P/G</Th>
            <Th className="text-center">Side</Th>
            <Th className="text-right">Qty</Th>
            <Th className="text-right">Price</Th>
            <Th className="text-right">Value</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => {
            const key = `${t.tradeId}-${i}`;
            const open = expanded.has(key);
            const traded = t.tradedAt ? formatTimestamp(t.tradedAt) : t.raw?.["Date/Time"] ?? "—";
            return (
              <Fragment key={key}>
                <tr className="border-t border-border/30 hover:bg-border/10">
                  <Td>
                    <button type="button" onClick={() => toggle(key)} aria-label="Toggle details" className="text-muted hover:text-fg w-4">
                      {open ? "▾" : "▸"}
                    </button>
                  </Td>
                  <Td>{traded}</Td>
                  <Td className="font-mono">{t.tradeId}</Td>
                  <Td>{t.venue ?? "—"}</Td>
                  <Td>{t.product}</Td>
                  <Td className="font-mono">{canonicalPeriod(t.period)}</Td>
                  <Td>{t.paidGiven ?? "—"}</Td>
                  <Td className={cn("text-center font-semibold", t.side === "B" ? "text-posGreen" : "text-negRed")}>{t.side}</Td>
                  <Td className="text-right tabular-nums">{t.signedQty}</Td>
                  <Td className="text-right tabular-nums">{Number(t.price)}</Td>
                  <Td className="text-right tabular-nums">{Number(t.value).toFixed(2)}</Td>
                </tr>
                {open && (
                  <tr className="bg-border/5">
                    <td colSpan={11} className="px-4 py-2">
                      <RawDetails raw={t.raw} signedQty={t.signedQty} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface HistoryRow {
  version: number;
  changeType: "edit" | "delete" | "reassign";
  snapshot: Record<string, unknown>;
  changedAt: string;
  changedByEmail: string | null;
  changedByName: string | null;
}

function TradeEditForm({ trade }: { trade: StoredTrade }) {
  const queryClient = useQueryClient();
  const [price, setPrice] = useState(String(Number(trade.price)));
  const [quantity, setQuantity] = useState(String(trade.quantity));
  const [side, setSide] = useState<"B" | "S">(trade.side);
  const [product, setProduct] = useState(trade.product);
  const [period, setPeriod] = useState(canonicalPeriod(trade.period));
  const [venue, setVenue] = useState(trade.venue ?? "");
  const [paidGiven, setPaidGiven] = useState(trade.paidGiven ?? "");
  const [tradedAt, setTradedAt] = useState(trade.tradedAt ?? "");
  const [msg, setMsg] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["eex-trades"] });
    invalidateEexData(queryClient); // tree/leaf, P&L months, worklist
    void queryClient.invalidateQueries({ queryKey: ["eex-change-requests"] });
    // applied edits/deletes change the VM ledger replay, delivery positions
    // and open remainders
    void queryClient.invalidateQueries({ queryKey: ["eex-margin"] });
    void queryClient.invalidateQueries({ queryKey: ["eex-delivery"] });
    void queryClient.invalidateQueries({ queryKey: ["eex-allocatable"] });
  };
  const edit = useMutation({
    mutationFn: (extras: GateExtras) =>
      api<{ pending: boolean }>(`/eex/trades/${trade.id}/edit`, {
        method: "PATCH",
        body: JSON.stringify({
          price: Number(price),
          quantity: Number(quantity),
          side,
          product,
          period,
          venue: venue.trim() || null,
          paidGiven: paidGiven.trim() || null,
          tradedAt: tradedAt.trim() || null,
          ...extras,
        }),
      }),
    onSuccess: (r) => {
      setMsg(r.pending ? "Submitted for admin approval." : "Saved.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const del = useMutation({
    mutationFn: (extras: GateExtras) =>
      api<{ pending: boolean }>(`/eex/trades/${trade.id}`, {
        method: "DELETE",
        body: JSON.stringify({ ...extras }),
      }),
    onSuccess: (r) => {
      setMsg(r.pending ? "Delete submitted for admin approval." : "Deleted.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });

  const busy = edit.isPending || del.isPending;
  const fieldCls = "bg-transparent border border-border/50 rounded px-1.5 py-0.5 text-xs";

  return (
    <div className="border border-border/40 rounded p-2 space-y-2">
      <div className="text-[10px] uppercase tracking-wide text-muted">
        Edit{trade.editNeedsApproval ? " — changes need admin approval" : ""}
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <Field label="Price"><input value={price} onChange={(e) => setPrice(e.target.value)} className={cn(fieldCls, "w-20")} /></Field>
        <Field label="Qty"><input value={quantity} onChange={(e) => setQuantity(e.target.value)} className={cn(fieldCls, "w-16")} /></Field>
        <Field label="Side">
          <select value={side} onChange={(e) => setSide(e.target.value as "B" | "S")} className={fieldCls}>
            <option value="B">B</option>
            <option value="S">S</option>
          </select>
        </Field>
        <Field label="Product"><input value={product} onChange={(e) => setProduct(e.target.value)} className={cn(fieldCls, "w-44")} /></Field>
        <Field label="Period"><input value={period} onChange={(e) => setPeriod(e.target.value)} className={cn(fieldCls, "w-24")} /></Field>
        <Field label="Traded"><input value={tradedAt} onChange={(e) => setTradedAt(e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" className={cn(fieldCls, "w-40")} /></Field>
        <Field label="Venue"><input value={venue} onChange={(e) => setVenue(e.target.value)} className={cn(fieldCls, "w-20")} /></Field>
        <Field label="P/G"><input value={paidGiven} onChange={(e) => setPaidGiven(e.target.value)} className={cn(fieldCls, "w-16")} /></Field>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => void withRecognitionConfirm((x) => edit.mutateAsync(x)).catch(() => {})}
          className="rounded bg-accent text-white px-3 py-1 text-xs disabled:opacity-50"
        >
          {trade.editNeedsApproval ? "Submit changes" : "Save changes"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (window.confirm(trade.editNeedsApproval ? "Submit a delete request for this trade?" : "Delete this trade?"))
              void withRecognitionConfirm((x) => del.mutateAsync(x)).catch(() => {});
          }}
          className="rounded border border-negRed/50 text-negRed px-3 py-1 text-xs disabled:opacity-50"
        >
          {trade.editNeedsApproval ? "Request delete" : "Delete"}
        </button>
        {msg && <span className="text-xs text-muted">{msg}</span>}
      </div>
    </div>
  );
}

function TradeHistory({ tradeId }: { tradeId: string }) {
  const canViewHistory = useFinanceFunction("trades.history");
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["eex-history", tradeId],
    enabled: open && canViewHistory,
    queryFn: () => api<{ history: HistoryRow[] }>(`/eex/trades/${tradeId}/history`),
  });
  const rows = q.data?.history ?? [];
  if (!canViewHistory) return null;
  return (
    <div className="text-xs">
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-accent hover:underline">
        {open ? "▾ Hide history" : "▸ Version history"}
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {q.isLoading && <div className="text-muted">Loading…</div>}
          {rows.length === 0 && !q.isLoading && <div className="text-muted">No prior versions.</div>}
          {rows.map((h, i) => (
            <div key={i} className="border border-border/40 rounded px-2 py-1">
              <div className="text-muted">
                v{h.version} · {h.changeType} · {formatTimestamp(h.changedAt)} · {h.changedByName || h.changedByEmail || "—"}
              </div>
              <div className="font-mono break-words text-[11px]">
                {["side", "signedQty", "price", "quantity", "product", "period", "book", "pairLabel", "tradedAt"]
                  .filter((k) => h.snapshot[k] != null && String(h.snapshot[k]) !== "")
                  .map((k) => `${k}=${k === "period" ? canonicalPeriod(String(h.snapshot[k])) : String(h.snapshot[k])}`)
                  .join("  ")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BilateralCapturedDetails({ trade }: { trade: StoredTrade }) {
  const status = trade.raw?.ProjectionStatus;
  const presentation = bilateralProjectionPresentation(status, trade.raw?.ProjectionReason);
  return (
    <div className="space-y-2">
      {status && (
        <div className="rounded border border-border/60 bg-border/5 p-2 text-xs">
          <div><span className="text-muted">EEX period mapping:</span> {presentation.label}</div>
          {presentation.description && <p className="mt-1 text-muted">{presentation.description}</p>}
        </div>
      )}
      <details className="rounded border border-border/40">
        <summary className="cursor-pointer px-2 py-2 text-xs font-medium">Original captured fields</summary>
        <div className="space-y-2 border-t border-border/40 p-2">
          <p className="text-xs text-muted">Captured when this row was created; retained for reference, not a live assessment of exposure in Positions.</p>
          <RawDetails raw={trade.raw} signedQty={trade.signedQty} />
        </div>
      </details>
    </div>
  );
}

function RawDetails({
  raw,
  signedQty,
}: {
  raw?: Record<string, string | null> | null;
  signedQty?: number;
}) {
  const entries = raw ? Object.entries(raw).filter(([, v]) => v != null && String(v).trim() !== "") : [];
  if (entries.length === 0) return <div className="text-muted text-xs">No additional captured fields.</div>;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1.5">
      {entries.map(([k, v]) => {
        // Show the platform's absolute Quantity as our signed, color-coded
        // position (+ bought / − sold) instead of the raw value.
        const isQty = signedQty != null && k.trim().toLowerCase() === "quantity";
        return (
          <div key={k} className="flex flex-col min-w-0">
            <span className="text-muted text-[10px] uppercase tracking-wide">{k}</span>
            {isQty ? (
              <DirectionLabel signedQty={signedQty} />
            ) : (
              <span className="font-mono break-words">{v}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
